import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import { useAuth } from '../AuthContext';
import { fetchLabourRates, calcDayworkSheetCost } from '../labourRates';
import { sendEmail } from '../utils/emailService';
import { DropZone } from '../components/DropZone';
import {
  dayworkAgreementEmail,
  dayworkApprovalNotification,
  dayworkChallengeNotification,
  variationAgreementEmail,
  variationResponseNotification,
  ewnCviResponseNotification,
  mcRaisedIrNotification,
  mcInstructionConfirmationEmail,
} from '../utils/emailTemplates';

const NAVY = '#000000';
const ACCENT = '#2563eb';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
}

function fmtDT(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function fmtCurrency(v) {
  if (v == null) return '—';
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(v);
}

function calcHours(start, finish) {
  if (!start || !finish) return 0;
  const [sh, sm] = start.split(':').map(Number);
  const [fh, fm] = finish.split(':').map(Number);
  const diff = (fh * 60 + fm) - (sh * 60 + sm);
  return diff > 0 ? Math.round(diff / 60 * 100) / 100 : 0;
}

// ─── Shared portal layout components ─────────────────────────────────────────

function PortalShell({ children }) {
  return (
    <div style={{ minHeight: '100vh', background: '#f3f4f6', fontFamily: "'Segoe UI', Arial, sans-serif" }}>
      {/* Header */}
      <div style={{ background: NAVY, padding: '20px 24px', marginBottom: '0' }}>
        <div style={{ maxWidth: 800, margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 700, color: '#fff', letterSpacing: '0.04em' }}>
              PROLINE INTERNALS LTD
            </div>
            <div style={{ fontSize: 11, color: '#a5b4fc', marginTop: 2, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              Commercial Management Portal
            </div>
          </div>
          <div style={{ fontSize: 11, color: '#94a3b8', textAlign: 'right', lineHeight: 1.5 }}>
            info@prolineinternals.co.uk
          </div>
        </div>
      </div>
      <div style={{ maxWidth: 800, margin: '0 auto', padding: '24px 16px 40px' }}>
        {children}
      </div>
      <div style={{ borderTop: '1px solid #e5e7eb', padding: '16px 24px', textAlign: 'center', fontSize: 11, color: '#9ca3af', background: '#f8fafc' }}>
        Proline Internals Ltd · info@prolineinternals.co.uk ·{' '}
        This portal is confidential and intended solely for the named recipient.
      </div>
    </div>
  );
}

function Card({ children, style }) {
  return (
    <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #e5e7eb', padding: '20px 24px', marginBottom: 20, ...style }}>
      {children}
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <div style={{ fontSize: 13, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 12 }}>
      {children}
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div style={{ display: 'flex', gap: 8, marginBottom: 8, fontSize: 14, flexWrap: 'wrap' }}>
      <span style={{ fontWeight: 600, color: '#6b7280', minWidth: 160 }}>{label}</span>
      <span style={{ color: '#111', flex: 1 }}>{value || '—'}</span>
    </div>
  );
}

function AlertBox({ color, bg, border, children }) {
  return (
    <div style={{ background: bg, borderLeft: `4px solid ${border}`, padding: '14px 18px', borderRadius: '0 6px 6px 0', marginBottom: 20 }}>
      <p style={{ margin: 0, fontSize: 13, color, lineHeight: 1.7 }}>{children}</p>
    </div>
  );
}

function InvalidPage({ message }) {
  return (
    <PortalShell>
      <Card style={{ textAlign: 'center', padding: '48px 24px' }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🔒</div>
        <h2 style={{ color: '#111', marginBottom: 12 }}>Link Invalid or Expired</h2>
        <p style={{ color: '#6b7280', fontSize: 14, lineHeight: 1.6 }}>
          {message || 'This link is invalid or has expired. Please contact Proline Internals Ltd.'}
        </p>
        <p style={{ color: '#6b7280', fontSize: 13, marginTop: 16 }}>
          Email: <a href="mailto:info@prolineinternals.co.uk" style={{ color: ACCENT }}>info@prolineinternals.co.uk</a>
        </p>
      </Card>
    </PortalShell>
  );
}

function SuccessPage({ title, lines, email }) {
  return (
    <PortalShell>
      <Card style={{ textAlign: 'center', padding: '48px 24px' }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
        <h2 style={{ color: '#16a34a', marginBottom: 12 }}>{title}</h2>
        {lines.map((l, i) => (
          <p key={i} style={{ color: '#374151', fontSize: 14, lineHeight: 1.6, margin: '4px 0' }}>{l}</p>
        ))}
        {email && (
          <p style={{ color: '#6b7280', fontSize: 13, marginTop: 16 }}>
            A confirmation email has been sent to <strong>{email}</strong>.
          </p>
        )}
      </Card>
    </PortalShell>
  );
}

function ChallengedPage({ lines }) {
  return (
    <PortalShell>
      <Card style={{ textAlign: 'center', padding: '48px 24px' }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>📋</div>
        <h2 style={{ color: '#d97706', marginBottom: 12 }}>Challenge Recorded</h2>
        {lines.map((l, i) => (
          <p key={i} style={{ color: '#374151', fontSize: 14, lineHeight: 1.6, margin: '4px 0' }}>{l}</p>
        ))}
      </Card>
    </PortalShell>
  );
}

function AlreadyRespondedPage({ recordType, reference }) {
  return (
    <PortalShell>
      <Card style={{ textAlign: 'center', padding: '48px 24px' }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>✓</div>
        <h2 style={{ color: '#16a34a', marginBottom: 12 }}>Already Responded</h2>
        <p style={{ color: '#374151', fontSize: 14, lineHeight: 1.6, maxWidth: 480, margin: '0 auto 8px' }}>
          This {recordType}{reference ? ` (${reference})` : ''} has already been responded to by an authorised
          representative. No further action is required from you.
        </p>
        <p style={{ color: '#374151', fontSize: 14, lineHeight: 1.6, maxWidth: 480, margin: '0 auto 16px' }}>
          If you believe this is an error, please contact Proline Internals Ltd directly.
        </p>
        <p style={{ color: '#6b7280', fontSize: 13, marginTop: 16 }}>
          Email: <a href="mailto:info@prolineinternals.co.uk" style={{ color: ACCENT }}>info@prolineinternals.co.uk</a>
        </p>
      </Card>
    </PortalShell>
  );
}

const VALUATION_BASIS_LABELS = {
  contract_rates: 'Contract Rates', new_rates: 'New Rates',
  fair_valuation: 'Fair Valuation', dayworks: 'Dayworks', mixed: 'Mixed',
};
function fmtValuationBasis(value) {
  return VALUATION_BASIS_LABELS[value] || (value ? value.replace(/_/g, ' ') : '—');
}

// ─── Helper: fetch project notify emails ─────────────────────────────────────

async function getNotifyEmails(projectId) {
  const { data } = await supabase
    .from('project_users')
    .select('profiles(email, role)')
    .eq('project_id', projectId);
  return (data || [])
    .map(r => r.profiles)
    .filter(p => p && (p.role === 'proline_qs' || p.role === 'admin'))
    .map(p => p.email)
    .filter(Boolean);
}

// ─── MC Portal: Authenticated Dashboard ──────────────────────────────────────

const DashCard = ({ children, style }) => (
  <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #e5e7eb', padding: '20px 24px', marginBottom: 16, ...style }}>
    {children}
  </div>
);

const DashSection = ({ title, count, children, empty }) => (
  <DashCard>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: count != null || empty ? 14 : 4 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{title}</div>
      {count != null && <span style={{ background: count > 0 ? '#fee2e2' : '#f3f4f6', color: count > 0 ? '#991b1b' : '#6b7280', fontSize: 12, fontWeight: 700, padding: '2px 9px', borderRadius: 12 }}>{count}</span>}
    </div>
    {empty && count === 0 ? <p style={{ fontSize: 13, color: '#9ca3af', margin: 0 }}>Nothing to action</p> : children}
  </DashCard>
);

export function MCPortalDashboard() {
  const { profile, signOut } = useAuth();
  const [project, setProject] = useState(null);
  const [dayworks, setDayworks] = useState([]);
  const [variations, setVariations] = useState([]);
  const [ewnCvis, setEwnCvis] = useState([]);
  const [respondedEwnCvis, setRespondedEwnCvis] = useState([]);
  const [mcIrs, setMcIrs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [rates, setRates] = useState(null);

  // Raise IR modal
  const [showRaiseIr, setShowRaiseIr] = useState(false);
  const [irForm, setIrForm] = useState({
    description: '',
    date: new Date().toISOString().split('T')[0],
    time: '',
    location: '',
    gridlines: '',
    level: '',
    mc_ref: '',
  });
  const [irAttachments, setIrAttachments] = useState([]);
  const [irSaving, setIrSaving] = useState(false);
  const [irError, setIrError] = useState('');

  // Sign daywork modal
  const [signModal, setSignModal] = useState(null); // { dw, disputeMode, disputeReason, signerName, signerPosition, signerCompany, agreed }

  // Variation response modal
  const [varModal, setVarModal] = useState(null); // { variation, responseType, reason, agreedValue }

  // Daywork full-review modal
  const [reviewDwModal, setReviewDwModal] = useState(null);
  // Variation full-review modal
  const [reviewVarModal, setReviewVarModal] = useState(null);

  // EWN/CVI response modal
  const [ewnModal, setEwnModal] = useState(null); // { instr, selectedResponse, notes, expectedDate, queryText, disputeText }
  const [ewnAttachments, setEwnAttachments] = useState([]);
  const [ewnAttachmentsLoading, setEwnAttachmentsLoading] = useState(false);
  const [ewnNoticeExpanded, setEwnNoticeExpanded] = useState(false);

  const [linkedIRsByParent, setLinkedIRsByParent] = useState({});
  const [irLinkedEwnCviId, setIrLinkedEwnCviId] = useState(null);
  const [irLinkedEwnCviRef, setIrLinkedEwnCviRef] = useState('');

  // IR detail view modal
  const [irDetailModal, setIrDetailModal] = useState(null);
  const [irDetailAtts, setIrDetailAtts] = useState([]);
  const [irDetailAttsLoading, setIrDetailAttsLoading] = useState(false);

  useEffect(() => {
    if (profile?.requested_project_id) load();
    else setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  useEffect(() => {
    if (!ewnModal?.instr?.id) { setEwnAttachments([]); return; }
    setEwnAttachmentsLoading(true);
    setEwnNoticeExpanded(false);
    supabase.from('instruction_attachments').select('*').eq('instruction_id', ewnModal.instr.id)
      .then(async ({ data }) => {
        const atts = data || [];
        const withUrls = await Promise.all(atts.map(async a => {
          const { data: sd } = await supabase.storage.from('instruction-attachments').createSignedUrl(a.storage_path, 604800);
          return { ...a, signedUrl: sd?.signedUrl || null };
        }));
        setEwnAttachments(withUrls);
        setEwnAttachmentsLoading(false);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ewnModal?.instr?.id]);

  useEffect(() => {
    if (!irDetailModal?.id) { setIrDetailAtts([]); return; }
    setIrDetailAttsLoading(true);
    supabase.from('instruction_attachments').select('*').eq('instruction_id', irDetailModal.id)
      .then(async ({ data }) => {
        const atts = data || [];
        const withUrls = await Promise.all(atts.map(async a => {
          const { data: sd } = await supabase.storage.from('instruction-attachments').createSignedUrl(a.storage_path, 604800);
          return { ...a, signedUrl: sd?.signedUrl || null };
        }));
        setIrDetailAtts(withUrls);
        setIrDetailAttsLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [irDetailModal?.id]);

  async function load() {
    const projectId = profile.requested_project_id;
    const [projRes, dwRes, varRes, ewnRes, respondedRes, irRes, linkedIrsRes] = await Promise.all([
      supabase.from('projects').select('*').eq('id', projectId).single(),
      supabase.from('dayworks').select('*, instructions(reference, type), daywork_labour(trade, hours_calculated, start_time, finish_time), daywork_materials(quantity, price_per_unit), daywork_plant(quantity, rate)').eq('project_id', projectId).eq('status', 'submitted').order('date_of_works'),
      supabase.from('variations').select('*, instructions(reference, type)').eq('project_id', projectId).eq('status', 'submitted').order('created_at'),
      supabase.from('instructions').select('*').eq('project_id', projectId).in('type', ['EWN', 'CVI']).is('mc_response', null).order('date', { ascending: false }),
      supabase.from('instructions').select('id, reference, type, date, mc_response, mc_response_at, mc_expected_instruction_date').eq('project_id', projectId).in('type', ['EWN', 'CVI']).not('mc_response', 'is', null).order('mc_response_at', { ascending: false }),
      supabase.from('instructions').select('*').eq('project_id', projectId).eq('raised_by_mc', true).order('created_at', { ascending: false }).limit(20),
      supabase.from('instructions').select('id, reference, linked_ewn_cvi_id, status, created_at').eq('project_id', projectId).eq('type', 'IR').not('linked_ewn_cvi_id', 'is', null),
    ]);
    setProject(projRes.data);
    setDayworks(dwRes.data || []);
    setVariations(varRes.data || []);
    setEwnCvis(ewnRes.data || []);
    setRespondedEwnCvis(respondedRes.data || []);
    setMcIrs(irRes.data || []);
    const lbp = {};
    (linkedIrsRes.data || []).forEach(ir => { lbp[ir.linked_ewn_cvi_id] = ir; });
    setLinkedIRsByParent(lbp);
    fetchLabourRates(projectId).then(setRates).catch(console.error);
    setLoading(false);
  }

  function flash(msg) { setMessage(msg); setTimeout(() => setMessage(''), 4000); }

  async function generateIrRef() {
    const { data } = await supabase.from('instructions').select('reference').eq('project_id', profile.requested_project_id).eq('type', 'IR');
    const existing = (data || []).map(r => { const m = r.reference?.match(/(\d+)$/); return m ? parseInt(m[1], 10) : 0; });
    const next = existing.length > 0 ? Math.max(...existing) + 1 : 1;
    const code = project?.project_code || project?.reference || 'PRJ';
    return `IR-${code}-${String(next).padStart(3, '0')}`;
  }

  async function handleRaiseIr() {
    if (!irForm.date) { setIrError('Date of instruction is required.'); return; }
    if (!irForm.description.trim()) { setIrError('Description is required.'); return; }
    setIrSaving(true);
    setIrError('');
    try {
      const ref = await generateIrRef();
      const { data: newInstr, error: instrErr } = await supabase.from('instructions').insert({
        project_id: profile.requested_project_id,
        type: 'IR',
        reference: ref,
        description: irForm.description.trim(),
        date: irForm.date,
        time: irForm.time || null,
        location: irForm.location.trim() || null,
        gridlines: irForm.gridlines.trim() || null,
        level: irForm.level.trim() || null,
        instruction_method: 'portal',
        raised_by_mc: true,
        mc_instruction_ref: irForm.mc_ref.trim() || null,
        status: 'open',
        created_by: profile.id,
        linked_ewn_cvi_id: irLinkedEwnCviId || null,
        instructed_by_name: profile.full_name,
        instructed_by_role: 'Main Contractor Representative',
      }).select().single();
      if (instrErr) throw instrErr;

      for (const file of irAttachments) {
        try {
          const path = `${newInstr.id}/${Date.now()}_${file.name}`;
          const { error: uploadErr } = await supabase.storage.from('instruction-attachments').upload(path, file);
          if (!uploadErr) {
            await supabase.from('instruction_attachments').insert({
              instruction_id: newInstr.id,
              file_name: file.name,
              storage_path: path,
              file_size: file.size,
              content_type: file.type,
              uploaded_by: profile.id,
            });
          }
        } catch (e) { console.error('File upload failed:', e); }
      }

      try {
        const { subject, htmlBody } = mcRaisedIrNotification(ref, irForm.mc_ref.trim(), irForm.description.trim(), project?.name, profile.full_name, profile.company, irForm.date, irLinkedEwnCviRef || null);
        await sendEmail({ to: 'notifications@prolineinternals.co.uk', subject, htmlBody });
      } catch (e) { console.error('IR notification email failed:', e); }

      try {
        const { subject, htmlBody } = mcInstructionConfirmationEmail({
          reference: ref,
          mcRef: irForm.mc_ref.trim() || null,
          description: irForm.description.trim(),
          projectName: project?.name,
          date: irForm.date,
          time: irForm.time || null,
          location: irForm.location.trim() || null,
          linkedRef: irLinkedEwnCviRef || null,
          recipientName: profile.full_name,
        });
        await sendEmail({ to: profile.email, subject, htmlBody });
      } catch (e) { console.error('IR confirmation email failed:', e); }

      setShowRaiseIr(false);
      setIrForm({
        description: '',
        date: new Date().toISOString().split('T')[0],
        time: '',
        location: '',
        gridlines: '',
        level: '',
        mc_ref: '',
      });
      setIrAttachments([]);
      const wasLinked = irLinkedEwnCviId;
      const linkedRef = irLinkedEwnCviRef;
      setIrLinkedEwnCviId(null);
      setIrLinkedEwnCviRef('');
      flash(wasLinked ? `Instruction ${ref} raised and linked to ${linkedRef}` : `Instruction ${ref} submitted to Proline Internals Ltd`);
      await load();
    } catch (err) {
      setIrError(err.message);
    } finally {
      setIrSaving(false);
    }
  }

  async function handleSignDaywork() {
    if (!signModal) return;
    const { dw, disputeMode, disputeReason, signerName, signerPosition, signerCompany, agreed } = signModal;
    if (!signerName.trim() || !signerCompany.trim()) { flash('Name and company are required.'); return; }
    if (disputeMode && !disputeReason.trim()) { flash('Please provide a dispute reason.'); return; }
    if (!disputeMode && !agreed) { flash('Please confirm by checking the checkbox.'); return; }
    const now = new Date().toISOString();
    try {
      if (disputeMode) {
        await supabase.from('dayworks').update({ status: 'disputed', dispute_reason: disputeReason.trim(), signed_by: profile.id, signed_at: now }).eq('id', dw.id);
        await supabase.from('daywork_approvals').insert({
          daywork_id: dw.id, action: 'challenged', actioned_at: now,
          full_name_confirmed: signerName.trim(), company_confirmed: signerCompany.trim(),
          challenge_reason: disputeReason.trim(), user_agent: navigator.userAgent,
        });
        try {
          const { subject, htmlBody } = dayworkChallengeNotification(dw, project, disputeReason.trim(), signerName.trim(), signerCompany.trim());
          await sendEmail({ to: 'notifications@prolineinternals.co.uk', subject, htmlBody });
        } catch (e) { console.error('Challenge notification failed:', e); }
      } else {
        await supabase.from('dayworks').update({ status: 'signed', signed_by: profile.id, signed_at: now }).eq('id', dw.id);
        await supabase.from('daywork_approvals').insert({
          daywork_id: dw.id, action: 'approved', actioned_at: now,
          full_name_confirmed: signerName.trim(), company_confirmed: signerCompany.trim(),
          user_agent: navigator.userAgent,
        });
        try {
          const { subject: s1, htmlBody: b1 } = dayworkApprovalNotification(dw, project, signerName.trim(), signerCompany.trim(), now);
          await sendEmail({ to: 'notifications@prolineinternals.co.uk', subject: s1, htmlBody: b1 });
        } catch (e) { console.error('Approval notification failed:', e); }
        try {
          const { subject: s2, htmlBody: b2 } = dayworkAgreementEmail(dw, project, signerName.trim(), now);
          await sendEmail({ to: profile.email, subject: s2, htmlBody: b2 });
        } catch (e) { console.error('Agreement email failed:', e); }
      }
      setSignModal(null);
      setReviewDwModal(null);
      flash(disputeMode ? `${dw.reference} disputed` : `${dw.reference} signed`);
      load();
    } catch (err) {
      flash('Error: ' + err.message);
    }
  }

  async function handleVarResponse() {
    if (!varModal || !varModal.responseType) return;
    const { variation, responseType, reason, agreedValue } = varModal;
    if ((responseType === 'part_agreed' || responseType === 'disputed') && !reason.trim()) return;
    if (responseType === 'part_agreed' && (!agreedValue || isNaN(parseFloat(agreedValue)))) return;
    const now = new Date().toISOString();
    const finalValue = responseType === 'agreed' ? variation.value : responseType === 'part_agreed' ? parseFloat(agreedValue) : null;
    try {
      await supabase.from('variations').update({
        status: responseType,
        mc_response: responseType,
        mc_response_at: now,
        mc_response_value: finalValue,
        mc_response_reason: reason.trim() || null,
        mc_responded_by: profile.id,
        mc_responded_at: now,
      }).eq('id', variation.id);
      await supabase.from('variation_approvals').insert({
        variation_id: variation.id,
        revision_number: variation.current_revision || 0,
        action: responseType,
        actioned_at: now,
        agreed_value: finalValue,
        response_reason: reason.trim() || null,
        full_name_confirmed: profile.full_name,
        company_confirmed: profile.company,
        user_agent: navigator.userAgent,
      });
      try {
        const { subject, htmlBody } = variationResponseNotification(variation, project, responseType, profile.full_name, profile.company, reason.trim(), finalValue);
        await sendEmail({ to: 'notifications@prolineinternals.co.uk', subject, htmlBody });
      } catch (e) { console.error('Var response notification failed:', e); }
      if (responseType === 'agreed' || responseType === 'part_agreed') {
        try {
          const { subject, htmlBody } = variationAgreementEmail(variation, project, profile.full_name, now, finalValue);
          await sendEmail({ to: profile.email, subject, htmlBody });
        } catch (e) { console.error('Var agreement email failed:', e); }
      }
      setVarModal(null);
      setReviewVarModal(null);
      flash(`Response recorded for ${variation.reference}`);
      load();
    } catch (err) {
      flash('Error: ' + err.message);
    }
  }

  async function handleEwnResponse() {
    console.log('[handleEwnResponse] called, selectedResponse:', ewnModal?.selectedResponse);
    if (!ewnModal || !ewnModal.selectedResponse) {
      console.log('[handleEwnResponse] early exit: no modal or no selectedResponse');
      return;
    }
    const { instr, selectedResponse, notes, expectedDate, queryText, disputeText } = ewnModal;
    const now = new Date().toISOString();
    const responseNotes = selectedResponse === 'query' ? queryText : selectedResponse === 'disputed' ? disputeText : notes || null;
    if ((selectedResponse === 'query' || selectedResponse === 'disputed') && !responseNotes?.trim()) {
      console.log('[handleEwnResponse] early exit: required notes missing');
      return;
    }
    try {
      const newStatus = selectedResponse === 'will_instruct' ? 'awaiting_instruction' : 'open';

      // Update ewn_cvi_responses if a token record exists for this instruction
      const { error: ewnErr } = await supabase
        .from('ewn_cvi_responses')
        .update({
          response: selectedResponse,
          response_notes: responseNotes || null,
          expected_instruction_date: selectedResponse === 'will_instruct' && expectedDate ? expectedDate : null,
          responded_at: now,
          recipient_name: profile.full_name,
          recipient_company: profile.company,
          recipient_email: profile.email,
        })
        .eq('instruction_id', instr.id)
        .is('responded_at', null);
      if (ewnErr) console.error('[handleEwnResponse] ewn_cvi_responses update failed:', ewnErr);

      // Update instruction mc_response fields
      const { error: instrErr } = await supabase.from('instructions').update({
        mc_response: selectedResponse,
        mc_response_at: now,
        mc_response_notes: responseNotes || null,
        mc_expected_instruction_date: selectedResponse === 'will_instruct' && expectedDate ? expectedDate : null,
        status: newStatus,
      }).eq('id', instr.id);
      if (instrErr) {
        console.error('[handleEwnResponse] instructions update failed:', instrErr);
        throw new Error(instrErr.message);
      }

      try {
        const { subject, htmlBody } = ewnCviResponseNotification({
          instruction: instr,
          response: selectedResponse,
          responseNotes: responseNotes || '',
          expectedDate: selectedResponse === 'will_instruct' ? expectedDate : null,
          respondedBy: profile.full_name,
          respondedCompany: profile.company,
          respondedAt: now,
        });
        await sendEmail({ to: 'notifications@prolineinternals.co.uk', subject, htmlBody });
      } catch (e) { console.error('EWN response notification failed:', e); }

      setEwnModal(null);
      flash(`Response recorded for ${instr.reference}`);
      load();
    } catch (err) {
      console.error('[handleEwnResponse] outer catch:', err);
      flash('Error: ' + err.message);
    }
  }

  async function handleOpenDwReview(dw) {
    setReviewDwModal({ dw, loading: true });
    try {
      const [{ data: fullDw }, { data: lab }, { data: mat }, { data: pla }] = await Promise.all([
        supabase.from('dayworks').select('*, instructions(*)').eq('id', dw.id).single(),
        supabase.from('daywork_labour').select('*').eq('daywork_id', dw.id).order('id'),
        supabase.from('daywork_materials').select('*').eq('daywork_id', dw.id).order('id'),
        supabase.from('daywork_plant').select('*').eq('daywork_id', dw.id).order('id'),
      ]);
      let attachments = [];
      if (fullDw?.instructions?.id) {
        const { data: atts } = await supabase.from('instruction_attachments').select('*').eq('instruction_id', fullDw.instructions.id);
        attachments = await Promise.all((atts || []).map(async a => {
          const { data: sd } = await supabase.storage.from('instruction-attachments').createSignedUrl(a.storage_path, 604800);
          return { ...a, signedUrl: sd?.signedUrl || null };
        }));
      }
      let dwMcInstrAuth = null;
      if (fullDw?.mc_instruction_authority) {
        try { dwMcInstrAuth = JSON.parse(fullDw.mc_instruction_authority); } catch {}
      }
      setReviewDwModal({ dw, loading: false, fullDw, mcInstrAuth: dwMcInstrAuth, labour: lab || [], materials: mat || [], plant: pla || [], instruction: fullDw?.instructions || null, attachments });
    } catch (err) {
      setReviewDwModal(null);
      flash('Error loading review: ' + err.message);
    }
  }

  async function handleOpenVarReview(variation) {
    setReviewVarModal({ variation, loading: true, attachments: [] });
    try {
      const { data: atts } = await supabase.from('variation_attachments').select('*').eq('variation_id', variation.id).order('uploaded_at');
      const withUrls = await Promise.all((atts || []).map(async a => {
        const { data: sd } = await supabase.storage.from('variation-attachments').createSignedUrl(a.storage_path, 604800);
        return { ...a, signedUrl: sd?.signedUrl || null };
      }));
      setReviewVarModal({ variation, loading: false, attachments: withUrls });
    } catch (err) {
      setReviewVarModal(null);
      flash('Error loading review: ' + err.message);
    }
  }

  if (loading) return (
    <div style={{ minHeight: '100vh', background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <p style={{ color: '#6b7280' }}>Loading…</p>
    </div>
  );

  if (!profile?.requested_project_id) return (
    <PortalShell>
      <DashCard style={{ textAlign: 'center', padding: '48px 24px' }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🏗️</div>
        <h2 style={{ color: '#374151', marginBottom: 12 }}>No Project Assigned</h2>
        <p style={{ color: '#6b7280', fontSize: 14, lineHeight: 1.6, marginBottom: 24 }}>You have not been assigned to a project yet. Please contact Proline Internals Ltd.</p>
        <button onClick={signOut} style={{ padding: '10px 24px', background: '#000', color: '#fff', border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Sign Out</button>
      </DashCard>
    </PortalShell>
  );

  const varResponseOptions = [
    { id: 'agreed', label: 'Agree in Full', color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0' },
    { id: 'part_agreed', label: 'Part Agree', color: '#d97706', bg: '#fffbeb', border: '#fde68a' },
    { id: 'disputed', label: 'Dispute', color: '#dc2626', bg: '#fef2f2', border: '#fecaca' },
  ];

  const ewnResponseCards = [
    { id: 'will_instruct', label: 'We Will Issue a Formal Instruction', sub: 'Confirm you will issue a written instruction', activeColor: '#16a34a', activeBg: '#f0fdf4', activeBorder: '#16a34a' },
    { id: 'query', label: 'We Have a Query', sub: 'You have a question before responding', activeColor: '#d97706', activeBg: '#fffbeb', activeBorder: '#d97706' },
    { id: 'disputed', label: 'We Dispute This Notice', sub: 'You dispute the basis of this notice', activeColor: '#dc2626', activeBg: '#fef2f2', activeBorder: '#dc2626' },
  ];

  const inputStyle = { width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' };
  const btnBase = { border: 'none', borderRadius: 6, padding: '9px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer' };

  return (
    <div style={{ minHeight: '100vh', background: '#f3f4f6', fontFamily: "'Segoe UI', Arial, sans-serif" }}>
      {/* Header */}
      <div style={{ background: '#000000', height: 56, display: 'flex', alignItems: 'center', padding: '0 24px', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#ffffff', lineHeight: 1.2 }}>PROLINE INTERNALS LTD</div>
          <div style={{ fontSize: 11, color: '#999999', letterSpacing: '0.08em', textTransform: 'uppercase', lineHeight: 1.2 }}>MC PORTAL</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#ffffff', lineHeight: 1.2 }}>{profile.full_name}</div>
            <div style={{ fontSize: 12, color: '#999999', lineHeight: 1.2 }}>{profile.company}</div>
          </div>
          <button
            onClick={signOut}
            style={{ background: 'transparent', color: '#ffffff', border: '1px solid rgba(255,255,255,0.5)', borderRadius: 4, padding: '6px 14px', fontSize: 13, fontWeight: 500, cursor: 'pointer', transition: 'background 0.15s, color 0.15s' }}
            onMouseEnter={e => { e.currentTarget.style.background = '#ffffff'; e.currentTarget.style.color = '#000000'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#ffffff'; }}
          >Sign Out</button>
        </div>
      </div>

      {/* Project banner */}
      {project && (
        <div style={{ background: '#111827', padding: '16px 24px', borderBottom: '1px solid #1f2937' }}>
          <div style={{ maxWidth: 880, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
            <div>
              <div style={{ fontSize: 18, fontWeight: 600, color: '#fff' }}>{project.name}</div>
              {project.main_contractor && <div style={{ fontSize: 13, color: '#9ca3af' }}>{project.main_contractor}</div>}
            </div>
            <div style={{ display: 'flex', gap: 24 }}>
              {[
                { label: 'Dayworks to Sign', value: dayworks.length, color: dayworks.length > 0 ? '#fbbf24' : '#9ca3af' },
                { label: 'Variations to Respond', value: variations.length, color: variations.length > 0 ? '#fbbf24' : '#9ca3af' },
                { label: 'Notices to Respond', value: ewnCvis.length, color: ewnCvis.length > 0 ? '#f87171' : '#9ca3af' },
                { label: 'Instructions Raised', value: mcIrs.length, color: '#9ca3af' },
              ].map(s => (
                <div key={s.label} style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color: s.color }}>{s.value}</div>
                  <div style={{ fontSize: 10, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div style={{ maxWidth: 880, margin: '0 auto', padding: '24px 16px 48px' }}>
        {message && <div style={{ background: '#d1fae5', border: '1px solid #6ee7b7', borderRadius: 6, padding: '10px 16px', marginBottom: 16, fontSize: 13, color: '#065f46' }}>{message}</div>}

        {/* Raise Instruction button */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
          <button onClick={() => { setIrLinkedEwnCviId(null); setIrLinkedEwnCviRef(''); setShowRaiseIr(true); }} style={{ ...btnBase, background: '#000', color: '#fff', padding: '10px 20px', fontSize: 14 }}>
            + Raise Instruction
          </button>
        </div>

        {/* EWN/CVI awaiting response */}
        <DashSection title="Notices Awaiting Your Response" count={ewnCvis.length} empty>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {ewnCvis.map(instr => (
              <div key={instr.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, gap: 12, flexWrap: 'wrap' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                    <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 3, textTransform: 'uppercase', ...(instr.type === 'EWN' ? { background: '#fffbeb', color: '#92400e' } : { background: '#dbeafe', color: '#1e40af' }) }}>{instr.type}</span>
                    <span style={{ fontWeight: 700, fontSize: 14 }}>{instr.reference}</span>
                  </div>
                  <div style={{ fontSize: 13, color: '#6b7280' }}>{instr.description?.substring(0, 100)}{instr.description?.length > 100 ? '…' : ''}</div>
                  {instr.date && <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{fmtDate(instr.date)}</div>}
                </div>
                <button
                  onClick={() => setEwnModal({ instr, selectedResponse: null, notes: '', expectedDate: '', queryText: '', disputeText: '' })}
                  style={{ ...btnBase, background: '#dc2626', color: '#fff', flexShrink: 0 }}
                >
                  Respond
                </button>
              </div>
            ))}
          </div>
        </DashSection>

        {/* Dayworks awaiting signature */}
        <DashSection title="Dayworks Awaiting Signature" count={dayworks.length} empty>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {dayworks.map(dw => {
              const lab = dw.daywork_labour || [];
              const totalHours = lab.reduce((s, r) => s + (r.hours_calculated || calcHours(r.start_time, r.finish_time) || 0), 0);
              const sheetValue = rates ? calcDayworkSheetCost(lab, dw.daywork_materials || [], dw.daywork_plant || [], rates) : null;
              const desc = dw.works_description || dw.description || null;
              return (
                <div key={dw.id} style={{ padding: '14px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 2 }}>{dw.reference}</div>
                      <div style={{ fontSize: 13, color: '#6b7280', marginBottom: desc ? 6 : 0 }}>
                        {fmtDate(dw.date_of_works)}{dw.instructions?.reference ? ` · ${dw.instructions.reference}` : ''}
                        {totalHours > 0 && <span style={{ marginLeft: 10, color: '#92400e', fontWeight: 600 }}>{totalHours.toFixed(2)}h</span>}
                        {sheetValue != null && <span style={{ marginLeft: 8, color: '#92400e', fontWeight: 600 }}>{fmtCurrency(sheetValue)}</span>}
                      </div>
                      {desc && <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.5, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{desc}</div>}
                    </div>
                    <div style={{ flexShrink: 0 }}>
                      <button onClick={() => handleOpenDwReview(dw)} style={{ ...btnBase, background: '#000', color: '#fff' }}>Review & Approve</button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </DashSection>

        {/* Variations awaiting response */}
        <DashSection title="Variations Awaiting Response" count={variations.length} empty>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {variations.map(v => (
              <div key={v.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 6, gap: 12, flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 2 }}>{v.reference}{v.title ? ` — ${v.title}` : ''}</div>
                  <div style={{ fontSize: 13, color: '#6b7280' }}>{v.instructions?.reference ? `${v.instructions.reference} · ` : ''}{v.value != null ? fmtCurrency(v.value) : '—'}</div>
                </div>
                <button onClick={() => handleOpenVarReview(v)} style={{ ...btnBase, background: '#2563eb', color: '#fff' }}>
                  Review & Respond
                </button>
              </div>
            ))}
          </div>
        </DashSection>

        {/* MC-raised IRs */}
        <DashSection title="Instructions Raised by You" count={null}>
          {mcIrs.length === 0 ? (
            <p style={{ fontSize: 13, color: '#9ca3af', margin: 0 }}>No instructions raised yet</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {mcIrs.map(ir => {
                const irStatusBadge = ir.status === 'open'
                  ? { label: 'Instruction Submitted', bg: '#dcfce7', color: '#166534', border: '#bbf7d0' }
                  : (ir.status === 'priced' || ir.status === 'daywork')
                  ? { label: 'Being Actioned', bg: '#dbeafe', color: '#1e40af', border: '#bfdbfe' }
                  : ir.status === 'closed'
                  ? { label: 'Completed', bg: '#f3f4f6', color: '#374151', border: '#e5e7eb' }
                  : { label: 'Instruction Submitted', bg: '#dcfce7', color: '#166534', border: '#bbf7d0' };
                const parentRef = ir.linked_ewn_cvi_id
                  ? (respondedEwnCvis.find(e => e.id === ir.linked_ewn_cvi_id)?.reference || null)
                  : null;
                return (
                  <div key={ir.id} style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '10px 14px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 6, gap: 12, flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 3 }}>{ir.reference}</div>
                      <div style={{ fontSize: 13, color: '#374151', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', lineHeight: 1.5 }}>
                        {ir.description || '—'}
                      </div>
                      {ir.mc_instruction_ref && <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 3 }}>Your Ref: {ir.mc_instruction_ref}</div>}
                      {parentRef && <div style={{ fontSize: 11, color: '#2563eb', marginTop: 3 }}>Linked to: {parentRef}</div>}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, flexShrink: 0 }}>
                      <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 10, background: irStatusBadge.bg, color: irStatusBadge.color, border: `1px solid ${irStatusBadge.border}` }}>
                        {irStatusBadge.label}
                      </span>
                      <button
                        onClick={() => setIrDetailModal(ir)}
                        style={{ background: '#0f172a', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                      >
                        View
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </DashSection>

        {/* Notices You Have Responded To */}
        {respondedEwnCvis.length > 0 && (
          <DashSection title="Notices You Have Responded To" count={null}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {respondedEwnCvis.map(instr => {
                const r = instr.mc_response;
                const linkedIR = r === 'will_instruct' ? linkedIRsByParent[instr.id] : null;
                const responseBadge = r === 'will_instruct'
                  ? (linkedIR
                    ? { label: 'Instruction Issued', bg: '#dcfce7', color: '#166534', border: '#bbf7d0' }
                    : { label: 'Will Issue Instruction', bg: '#fef3c7', color: '#92400e', border: '#fde68a' })
                  : r === 'query'
                  ? { label: 'Query Raised', bg: '#fef3c7', color: '#92400e', border: '#fde68a' }
                  : r === 'disputed'
                  ? { label: 'Disputed', bg: '#fef2f2', color: '#dc2626', border: '#fecaca' }
                  : { label: r, bg: '#f3f4f6', color: '#6b7280', border: '#e5e7eb' };
                return (
                  <div key={instr.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 6, gap: 12, flexWrap: 'wrap' }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                        <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 3, textTransform: 'uppercase', ...(instr.type === 'EWN' ? { background: '#fffbeb', color: '#92400e' } : { background: '#dbeafe', color: '#1e40af' }) }}>{instr.type}</span>
                        <span style={{ fontWeight: 700, fontSize: 14 }}>{instr.reference}</span>
                      </div>
                      {instr.date && <div style={{ fontSize: 11, color: '#9ca3af' }}>{fmtDate(instr.date)}</div>}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                      <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 10, background: responseBadge.bg, color: responseBadge.color, border: `1px solid ${responseBadge.border}` }}>
                        {responseBadge.label}
                      </span>
                      {instr.mc_response_at && <div style={{ fontSize: 11, color: '#9ca3af' }}>{fmtDT(instr.mc_response_at)}</div>}
                      {r === 'will_instruct' && linkedIR && (
                        <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 10, background: '#dcfce7', color: '#166534', border: '1px solid #bbf7d0' }}>
                          IR: {linkedIR.reference}
                        </span>
                      )}
                      {r === 'will_instruct' && !linkedIR && (
                        <>
                          {instr.mc_expected_instruction_date && (
                            <div style={{ fontSize: 11, color: '#92400e', marginTop: 2 }}>Expected: {fmtDate(instr.mc_expected_instruction_date)}</div>
                          )}
                          <button
                            onClick={() => { setIrLinkedEwnCviId(instr.id); setIrLinkedEwnCviRef(instr.reference); setShowRaiseIr(true); }}
                            style={{ background: '#111827', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 16px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
                          >
                            + Raise Formal Instruction
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </DashSection>
        )}
      </div>

      {/* ─── Raise IR Modal ─── */}
      {showRaiseIr && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: '#fff', borderRadius: 12, maxWidth: 680, width: '100%', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>

            {/* Modal header */}
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #e5e7eb', flexShrink: 0 }}>
              <h3 style={{ fontSize: 18, fontWeight: 700, margin: '0 0 4px', color: '#111' }}>Raise Formal Instruction</h3>
              <p style={{ fontSize: 13, color: '#6b7280', margin: 0 }}>Issue a formal written instruction to Proline Internals Ltd</p>
            </div>

            {/* Scrollable body */}
            <div style={{ overflowY: 'auto', flex: 1, padding: '20px 24px' }}>

              {/* Amber linked-notice banner */}
              {irLinkedEwnCviId && (
                <div style={{ background: '#fffbeb', border: '1px solid #f59e0b', borderRadius: 6, padding: '10px 14px', marginBottom: 16, fontSize: 14, color: '#92400e' }}>
                  This instruction will be linked to <strong>{irLinkedEwnCviRef}</strong>
                </div>
              )}

              {irError && (
                <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '10px 14px', marginBottom: 14, fontSize: 13, color: '#dc2626' }}>
                  {irError}
                </div>
              )}

              {/* ── Section 1: Instruction Details ── */}
              <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 20, marginBottom: 16 }}>
                <h4 style={{ fontSize: 15, fontWeight: 700, color: '#111', margin: '0 0 16px' }}>1. Instruction Details</h4>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Date of Instruction <span style={{ color: '#dc2626' }}>*</span></label>
                    <input type="date" value={irForm.date} onChange={e => setIrForm(f => ({ ...f, date: e.target.value }))} style={inputStyle} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Time</label>
                    <input type="time" value={irForm.time} onChange={e => setIrForm(f => ({ ...f, time: e.target.value }))} style={inputStyle} />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 14 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Location</label>
                    <input value={irForm.location} onChange={e => setIrForm(f => ({ ...f, location: e.target.value }))} style={inputStyle} placeholder="e.g. Level 3, Block B" />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Gridlines</label>
                    <input value={irForm.gridlines} onChange={e => setIrForm(f => ({ ...f, gridlines: e.target.value }))} style={inputStyle} placeholder="e.g. A3 to B5" />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Level / Floor</label>
                    <input value={irForm.level} onChange={e => setIrForm(f => ({ ...f, level: e.target.value }))} style={inputStyle} placeholder="e.g. Ground Floor" />
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Your Internal Reference <span style={{ fontSize: 11, fontWeight: 400, color: '#9ca3af' }}>(optional)</span></label>
                  <input value={irForm.mc_ref} onChange={e => setIrForm(f => ({ ...f, mc_ref: e.target.value }))} style={inputStyle} placeholder="e.g. SI-001, RFI-045" />
                  <p style={{ fontSize: 11, color: '#9ca3af', margin: '4px 0 0' }}>Your own reference number for this instruction if applicable</p>
                </div>
              </div>

              {/* ── Section 2: Description ── */}
              <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 20, marginBottom: 16 }}>
                <h4 style={{ fontSize: 15, fontWeight: 700, color: '#111', margin: '0 0 16px' }}>2. Description of Works Instructed</h4>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Description <span style={{ color: '#dc2626' }}>*</span></label>
                <textarea
                  value={irForm.description}
                  onChange={e => setIrForm(f => ({ ...f, description: e.target.value }))}
                  style={{ ...inputStyle, minHeight: 120, resize: 'vertical' }}
                  placeholder="Describe clearly what you are instructing Proline Internals Ltd to carry out. Be specific — include location, scope, any drawings or documents referenced."
                />
              </div>

              {/* ── Section 3: Supporting Document ── */}
              <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 20, marginBottom: 16 }}>
                <h4 style={{ fontSize: 15, fontWeight: 700, color: '#111', margin: '0 0 8px' }}>3. Supporting Document</h4>
                <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 14px', lineHeight: 1.6 }}>
                  Attach the instruction document (email, drawing, letter, etc). Attaching the original document is strongly recommended as it forms the contractual record of this instruction.
                </p>
                <DropZone
                  label="instruction document"
                  files={irAttachments}
                  onFilesSelected={files => setIrAttachments(prev => {
                    const existing = new Set(prev.map(f => f.name));
                    return [...prev, ...files.filter(f => !existing.has(f.name))];
                  })}
                  onRemove={i => setIrAttachments(prev => prev.filter((_, j) => j !== i))}
                  accept=".pdf,.doc,.docx,.msg,.eml,.jpg,.jpeg,.png"
                  multiple
                />
              </div>

              {/* ── Section 4: Your Details ── */}
              <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 20 }}>
                <h4 style={{ fontSize: 15, fontWeight: 700, color: '#111', margin: '0 0 16px' }}>4. Your Details</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  {[
                    { label: 'Name', value: profile.full_name },
                    { label: 'Company', value: profile.company },
                    { label: 'Email', value: profile.email },
                  ].map(({ label, value }) => (
                    <div key={label}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 3 }}>{label}</div>
                      <div style={{ fontSize: 14, color: '#111' }}>{value || '—'}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Sticky footer */}
            <div style={{ padding: '14px 20px', borderTop: '1px solid #e5e7eb', display: 'flex', gap: 10, justifyContent: 'flex-end', flexShrink: 0, background: '#fff', borderRadius: '0 0 12px 12px' }}>
              <button
                onClick={() => { setShowRaiseIr(false); setIrError(''); setIrAttachments([]); setIrLinkedEwnCviId(null); setIrLinkedEwnCviRef(''); }}
                style={{ ...btnBase, background: '#f3f4f6', color: '#374151' }}
                disabled={irSaving}
              >
                Cancel
              </button>
              <button
                onClick={handleRaiseIr}
                disabled={irSaving || !irForm.date || !irForm.description.trim()}
                style={{ ...btnBase, background: '#111827', color: '#fff', fontWeight: 700, opacity: (irSaving || !irForm.date || !irForm.description.trim()) ? 0.5 : 1 }}
              >
                {irSaving ? 'Submitting…' : 'Submit Instruction'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── IR Detail Modal ─── */}
      {irDetailModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: '#fff', borderRadius: 12, maxWidth: 680, width: '100%', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

            {/* Dark header */}
            <div style={{ background: '#0f172a', padding: '14px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 3, background: '#0f172a', color: '#e2e8f0', border: '1px solid #475569', textTransform: 'uppercase' }}>IR</span>
                <span style={{ fontWeight: 700, fontSize: 16, fontFamily: 'monospace', color: '#fff' }}>{irDetailModal.reference}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {irDetailModal.date && <span style={{ fontSize: 12, color: '#94a3b8' }}>{fmtDate(irDetailModal.date)}</span>}
                <button onClick={() => setIrDetailModal(null)} style={{ background: 'transparent', color: '#94a3b8', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 6, padding: '5px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Close</button>
              </div>
            </div>

            {/* Amber linked-notice banner */}
            {irDetailModal.linked_ewn_cvi_id && (() => {
              const pRef = respondedEwnCvis.find(e => e.id === irDetailModal.linked_ewn_cvi_id)?.reference;
              return pRef ? (
                <div style={{ background: '#fffbeb', borderBottom: '1px solid #f59e0b', padding: '8px 20px', fontSize: 13, color: '#92400e', flexShrink: 0 }}>
                  This instruction is linked to <strong>{pRef}</strong>
                </div>
              ) : null;
            })()}

            {/* Scrollable body */}
            <div style={{ overflowY: 'auto', flex: 1, padding: '20px 24px' }}>

              {/* Section 1 — Details */}
              <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 20, marginBottom: 16 }}>
                <h4 style={{ fontSize: 14, fontWeight: 700, color: '#111', margin: '0 0 16px' }}>Instruction Details</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
                  {[
                    { label: 'Project', value: project?.name },
                    { label: 'Date of Instruction', value: irDetailModal.date ? (irDetailModal.time ? `${fmtDate(irDetailModal.date)} at ${String(irDetailModal.time).substring(0, 5)}` : fmtDate(irDetailModal.date)) : '—' },
                    { label: 'Location', value: [irDetailModal.location, irDetailModal.level].filter(Boolean).join(' · ') || '—' },
                    { label: 'Method', value: 'Portal submission' },
                    { label: 'Your Reference', value: irDetailModal.mc_instruction_ref || '—' },
                    { label: 'Submitted', value: fmtDT(irDetailModal.created_at) },
                  ].map(({ label, value }) => (
                    <div key={label} style={{ display: 'flex', gap: 8, fontSize: 14 }}>
                      <span style={{ fontWeight: 600, color: '#6b7280', minWidth: 180, flexShrink: 0 }}>{label}</span>
                      <span style={{ color: '#111' }}>{value}</span>
                    </div>
                  ))}
                  <div style={{ display: 'flex', gap: 8, fontSize: 14, alignItems: 'center' }}>
                    <span style={{ fontWeight: 600, color: '#6b7280', minWidth: 180, flexShrink: 0 }}>Status</span>
                    {(() => {
                      const s = irDetailModal.status;
                      const b = s === 'open' ? { label: 'Awaiting Valuation', bg: '#fffbeb', color: '#92400e' }
                        : (s === 'priced' || s === 'daywork') ? { label: 'Being Valued', bg: '#dbeafe', color: '#1e40af' }
                        : s === 'closed' ? { label: 'Closed', bg: '#f0fdf4', color: '#065f46' }
                        : { label: s || 'Open', bg: '#f3f4f6', color: '#6b7280' };
                      return <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 10, background: b.bg, color: b.color }}>{b.label}</span>;
                    })()}
                  </div>
                </div>
                {irDetailModal.description && (
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Description of Works</div>
                    <div style={{ background: '#f9fafb', borderRadius: 6, padding: 12, fontSize: 13, color: '#374151', whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>{irDetailModal.description}</div>
                  </div>
                )}
              </div>

              {/* Section 2 — Supporting Documents */}
              <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 20, marginBottom: 16 }}>
                <h4 style={{ fontSize: 14, fontWeight: 700, color: '#111', margin: '0 0 12px' }}>Supporting Documents</h4>
                {irDetailAttsLoading ? (
                  <p style={{ fontSize: 13, color: '#9ca3af', margin: 0 }}>Loading…</p>
                ) : irDetailAtts.length === 0 ? (
                  <p style={{ fontSize: 13, color: '#9ca3af', margin: 0 }}>No documents attached.</p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {irDetailAtts.map(a => (
                      <div key={a.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 6, gap: 12 }}>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: '#111' }}>📄 {a.file_name}</div>
                          <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>
                            {a.file_size ? `${Math.round(a.file_size / 1024)} KB` : ''}{a.content_type ? ` · ${a.content_type}` : ''}
                          </div>
                        </div>
                        {a.signedUrl && (
                          <a href={a.signedUrl} target="_blank" rel="noopener noreferrer"
                            style={{ fontSize: 12, fontWeight: 600, color: '#2563eb', textDecoration: 'none', whiteSpace: 'nowrap' }}>
                            Download ↓
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Section 3 — Proline Response */}
              <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 20 }}>
                <h4 style={{ fontSize: 14, fontWeight: 700, color: '#111', margin: '0 0 12px' }}>Proline Response</h4>
                {(() => {
                  const s = irDetailModal.status;
                  if (s === 'open') return (
                    <div style={{ background: '#fffbeb', borderLeft: '4px solid #f59e0b', padding: '12px 16px', borderRadius: '0 6px 6px 0' }}>
                      <p style={{ margin: 0, fontSize: 13, color: '#92400e', lineHeight: 1.7 }}>
                        Proline Internals Ltd has received this instruction and will be in touch once it has been reviewed and valued.
                      </p>
                    </div>
                  );
                  if (s === 'priced' || s === 'daywork') return (
                    <div style={{ background: '#dbeafe', borderLeft: '4px solid #2563eb', padding: '12px 16px', borderRadius: '0 6px 6px 0' }}>
                      <p style={{ margin: 0, fontSize: 13, color: '#1e40af', lineHeight: 1.7 }}>
                        This instruction is being valued. You will receive a submission for your review shortly.
                      </p>
                    </div>
                  );
                  if (s === 'closed') return (
                    <div style={{ background: '#f0fdf4', borderLeft: '4px solid #16a34a', padding: '12px 16px', borderRadius: '0 6px 6px 0' }}>
                      <p style={{ margin: 0, fontSize: 13, color: '#065f46', lineHeight: 1.7 }}>
                        This instruction has been completed and closed.
                      </p>
                    </div>
                  );
                  return <p style={{ margin: 0, fontSize: 13, color: '#9ca3af' }}>No response recorded yet.</p>;
                })()}
              </div>
            </div>

            {/* Footer */}
            <div style={{ padding: '14px 20px', borderTop: '1px solid #e5e7eb', flexShrink: 0, background: '#fff', borderRadius: '0 0 12px 12px' }}>
              <button onClick={() => setIrDetailModal(null)} style={{ width: '100%', padding: '10px', background: 'transparent', color: '#374151', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Daywork Review Modal ─── */}
      {reviewDwModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: '#fff', borderRadius: 12, maxWidth: 800, width: '100%', maxHeight: '95vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

            {/* Header */}
            <div style={{ background: '#000', padding: '16px 24px', flexShrink: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Daywork Sheet — Full Review</div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: '#fff', fontFamily: 'monospace' }}>{reviewDwModal.dw.reference}</div>
                  {!reviewDwModal.loading && reviewDwModal.fullDw && (
                    <div style={{ fontSize: 13, color: '#cbd5e1', marginTop: 4 }}>{fmtDate(reviewDwModal.fullDw.date_of_works)}</div>
                  )}
                </div>
                <button onClick={() => setReviewDwModal(null)} style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.3)', color: '#94a3b8', borderRadius: 6, padding: '6px 14px', fontSize: 13, cursor: 'pointer' }}>Close</button>
              </div>
            </div>

            {/* Scrollable body */}
            <div style={{ overflowY: 'auto', flex: 1, padding: '20px 24px', background: '#f3f4f6' }}>
              {reviewDwModal.loading ? (
                <div style={{ textAlign: 'center', padding: 48, color: '#6b7280' }}>Loading document…</div>
              ) : (() => {
                const { fullDw, mcInstrAuth: dwAuth, labour: rl, materials: rm, plant: rp, instruction: ri, attachments: ra } = reviewDwModal;
                const rTotalHours = rl.reduce((s, r) => s + (parseFloat(r.hours_calculated) || calcHours(r.start_time, r.finish_time)), 0);
                const rLabCost = rates ? rl.reduce((s, r) => s + (parseFloat(r.hours_calculated) || calcHours(r.start_time, r.finish_time)) * rates.getRateForTrade(r.trade), 0) : null;
                const rMatTotal = rates ? rm.reduce((s, r) => s + (parseFloat(r.quantity) || 0) * (parseFloat(r.price_per_unit) || 0), 0) * (1 + rates.materialsUplift / 100) : null;
                const rPlantTotal = rates ? rp.reduce((s, r) => s + (parseFloat(r.quantity) || 0) * (parseFloat(r.rate) || 0), 0) * (1 + rates.plantUplift / 100) : null;
                const rGrandTotal = rLabCost != null ? rLabCost + (rMatTotal || 0) + (rPlantTotal || 0) : null;
                const sectionStyle = { background: '#fff', borderRadius: 8, border: '1px solid #e5e7eb', padding: '18px 20px', marginBottom: 16 };
                const secTitle = (t) => <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 12 }}>{t}</div>;
                const infoR = (label, value) => value ? (
                  <div style={{ display: 'flex', gap: 8, marginBottom: 8, fontSize: 14 }}>
                    <span style={{ fontWeight: 600, color: '#6b7280', minWidth: 160 }}>{label}</span>
                    <span style={{ color: '#111', flex: 1 }}>{value}</span>
                  </div>
                ) : null;
                return (
                  <>
                    {/* 1. Daywork Details */}
                    <div style={sectionStyle}>
                      {secTitle('Daywork Details')}
                      {infoR('Reference', fullDw.reference)}
                      {infoR('Date of Works', fmtDate(fullDw.date_of_works))}
                      {infoR('Location', [fullDw.location, fullDw.level && `Level: ${fullDw.level}`, fullDw.gridlines && `Gridlines: ${fullDw.gridlines}`].filter(Boolean).join(' · ') || null)}
                      {ri && infoR('Instruction', `${ri.reference}${ri.type ? ` (${ri.type})` : ''}`)}
                    </div>

                    {/* 2. Authority for Works */}
                    {dwAuth && (
                      <div style={sectionStyle}>
                        {secTitle('Authority for These Works')}
                        {[
                          ['Reference', dwAuth.reference],
                          ['Type', dwAuth.type],
                          ['Date', dwAuth.date ? fmtDate(dwAuth.date) : null],
                          ['Time', dwAuth.time],
                          ['Instructed By', [dwAuth.instructed_by_name, dwAuth.instructed_by_role].filter(Boolean).join(' — ') || null],
                          ['Method', dwAuth.instruction_method],
                          ['Main Contractor', dwAuth.main_contractor],
                        ].filter(([, v]) => v).map(([l, v]) => infoR(l, v))}
                        {dwAuth.description && (
                          <div style={{ marginTop: 8, background: '#f9fafb', borderRadius: 6, padding: '10px 12px', fontSize: 13, color: '#374151', lineHeight: 1.6 }}>{dwAuth.description}</div>
                        )}
                      </div>
                    )}

                    {/* 3. Description of Works */}
                    {(fullDw.works_description || fullDw.description) && (
                      <div style={sectionStyle}>
                        {secTitle('Description of Works Carried Out')}
                        <p style={{ fontSize: 13, color: '#374151', lineHeight: 1.6, margin: 0 }}>{fullDw.works_description || fullDw.description}</p>
                      </div>
                    )}

                    {/* 4. Reason Outside Scope */}
                    {fullDw.reason_outside_scope && (
                      <div style={{ borderLeft: '4px solid #f59e0b', background: '#fffbeb', borderRadius: '0 8px 8px 0', padding: '14px 18px', marginBottom: 16 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Reason Works are Outside Subcontract Scope</div>
                        <p style={{ margin: 0, fontSize: 13, color: '#78350f', lineHeight: 1.6 }}>{fullDw.reason_outside_scope}</p>
                      </div>
                    )}

                    {/* 5. Labour */}
                    {rl.length > 0 && (
                      <div style={sectionStyle}>
                        {secTitle(`Labour — ${rTotalHours.toFixed(2)} hrs`)}
                        <div style={{ overflowX: 'auto' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                            <thead>
                              <tr style={{ background: '#f9fafb' }}>
                                {['Operative', 'Trade', 'Start', 'Finish', 'Hours', 'Rate (£/h)', 'Cost (£)', 'Activity'].map(h => (
                                  <th key={h} style={{ padding: '7px 10px', textAlign: 'left', fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap', fontSize: 12 }}>{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {rl.map((r, i) => {
                                const hrs = parseFloat(r.hours_calculated) || calcHours(r.start_time, r.finish_time);
                                const rate = rates ? rates.getRateForTrade(r.trade) : null;
                                const cost = rate != null ? hrs * rate : null;
                                return (
                                  <tr key={r.id} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                                    <td style={{ padding: '7px 10px', borderBottom: '1px solid #f3f4f6' }}>{r.operative_name || '—'}</td>
                                    <td style={{ padding: '7px 10px', borderBottom: '1px solid #f3f4f6' }}>{r.trade || '—'}</td>
                                    <td style={{ padding: '7px 10px', borderBottom: '1px solid #f3f4f6', fontFamily: 'monospace' }}>{(r.start_time || '').substring(0, 5) || '—'}</td>
                                    <td style={{ padding: '7px 10px', borderBottom: '1px solid #f3f4f6', fontFamily: 'monospace' }}>{(r.finish_time || '').substring(0, 5) || '—'}</td>
                                    <td style={{ padding: '7px 10px', fontWeight: 700, color: '#16a34a', borderBottom: '1px solid #f3f4f6' }}>{hrs > 0 ? `${hrs.toFixed(2)}h` : '—'}</td>
                                    <td style={{ padding: '7px 10px', borderBottom: '1px solid #f3f4f6' }}>{rate != null ? `£${rate.toFixed(2)}` : '—'}</td>
                                    <td style={{ padding: '7px 10px', fontWeight: 600, borderBottom: '1px solid #f3f4f6' }}>{cost != null ? `£${cost.toFixed(2)}` : '—'}</td>
                                    <td style={{ padding: '7px 10px', color: '#6b7280', borderBottom: '1px solid #f3f4f6' }}>{r.activity_note || '—'}</td>
                                  </tr>
                                );
                              })}
                              <tr style={{ background: '#f0fdf4' }}>
                                <td colSpan={4} style={{ padding: '7px 10px', fontWeight: 700, color: '#16a34a', fontSize: 12 }}>Total Labour</td>
                                <td style={{ padding: '7px 10px', fontWeight: 700, color: '#16a34a' }}>{rTotalHours.toFixed(2)}h</td>
                                <td />
                                <td style={{ padding: '7px 10px', fontWeight: 700, color: '#16a34a' }}>{rLabCost != null ? `£${rLabCost.toFixed(2)}` : '—'}</td>
                                <td />
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* 6. Materials */}
                    {rm.filter(r => r.description?.trim()).length > 0 && (
                      <div style={sectionStyle}>
                        {secTitle('Materials')}
                        <div style={{ overflowX: 'auto' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                            <thead>
                              <tr style={{ background: '#f9fafb' }}>
                                {['Description', 'Qty', 'Unit', 'Rate (£)', 'Total (£)'].map(h => (
                                  <th key={h} style={{ padding: '7px 10px', textAlign: 'left', fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb', fontSize: 12 }}>{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {rm.filter(r => r.description?.trim()).map((r, i) => {
                                const sub = (parseFloat(r.quantity) || 0) * (parseFloat(r.price_per_unit) || 0);
                                const total = rates ? sub * (1 + rates.materialsUplift / 100) : sub;
                                return (
                                  <tr key={r.id} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                                    <td style={{ padding: '7px 10px', borderBottom: '1px solid #f3f4f6' }}>{r.description}</td>
                                    <td style={{ padding: '7px 10px', borderBottom: '1px solid #f3f4f6' }}>{r.quantity}</td>
                                    <td style={{ padding: '7px 10px', borderBottom: '1px solid #f3f4f6' }}>{r.unit || '—'}</td>
                                    <td style={{ padding: '7px 10px', borderBottom: '1px solid #f3f4f6' }}>£{parseFloat(r.price_per_unit || 0).toFixed(2)}</td>
                                    <td style={{ padding: '7px 10px', fontWeight: 600, borderBottom: '1px solid #f3f4f6' }}>£{total.toFixed(2)}</td>
                                  </tr>
                                );
                              })}
                              <tr style={{ background: '#f0fdf4' }}>
                                <td colSpan={4} style={{ padding: '7px 10px', fontWeight: 700, color: '#16a34a', fontSize: 12 }}>Total Materials</td>
                                <td style={{ padding: '7px 10px', fontWeight: 700, color: '#16a34a' }}>{rMatTotal != null ? `£${rMatTotal.toFixed(2)}` : '—'}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* 7. Plant */}
                    {rp.filter(r => r.description?.trim()).length > 0 && (
                      <div style={sectionStyle}>
                        {secTitle('Plant & Equipment')}
                        <div style={{ overflowX: 'auto' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                            <thead>
                              <tr style={{ background: '#f9fafb' }}>
                                {['Description', 'Qty', 'Unit', 'Rate (£)', 'Total (£)'].map(h => (
                                  <th key={h} style={{ padding: '7px 10px', textAlign: 'left', fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb', fontSize: 12 }}>{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {rp.filter(r => r.description?.trim()).map((r, i) => {
                                const sub = (parseFloat(r.quantity) || 0) * (parseFloat(r.rate) || 0);
                                const total = rates ? sub * (1 + rates.plantUplift / 100) : sub;
                                return (
                                  <tr key={r.id} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                                    <td style={{ padding: '7px 10px', borderBottom: '1px solid #f3f4f6' }}>{r.description}</td>
                                    <td style={{ padding: '7px 10px', borderBottom: '1px solid #f3f4f6' }}>{r.quantity}</td>
                                    <td style={{ padding: '7px 10px', borderBottom: '1px solid #f3f4f6' }}>{r.unit || '—'}</td>
                                    <td style={{ padding: '7px 10px', borderBottom: '1px solid #f3f4f6' }}>£{parseFloat(r.rate || 0).toFixed(2)}</td>
                                    <td style={{ padding: '7px 10px', fontWeight: 600, borderBottom: '1px solid #f3f4f6' }}>£{total.toFixed(2)}</td>
                                  </tr>
                                );
                              })}
                              <tr style={{ background: '#f0fdf4' }}>
                                <td colSpan={4} style={{ padding: '7px 10px', fontWeight: 700, color: '#16a34a', fontSize: 12 }}>Total Plant</td>
                                <td style={{ padding: '7px 10px', fontWeight: 700, color: '#16a34a' }}>{rPlantTotal != null ? `£${rPlantTotal.toFixed(2)}` : '—'}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* 8. Cost Summary */}
                    {rGrandTotal != null && (
                      <div style={{ background: '#000', borderRadius: 8, padding: '18px 20px', marginBottom: 16 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 14 }}>Cost Summary</div>
                        {[
                          ['Labour', rLabCost],
                          [`Materials${rates?.materialsUplift ? ` (${rates.materialsUplift}% uplift)` : ''}`, rMatTotal],
                          [`Plant${rates?.plantUplift ? ` (${rates.plantUplift}% uplift)` : ''}`, rPlantTotal],
                        ].map(([label, val]) => (
                          <div key={label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, color: '#e5e7eb', marginBottom: 8 }}>
                            <span>{label}</span><span>{val != null ? `£${val.toFixed(2)}` : '—'}</span>
                          </div>
                        ))}
                        <div style={{ borderTop: '1px solid rgba(255,255,255,0.2)', marginTop: 8, paddingTop: 10, display: 'flex', justifyContent: 'space-between', fontSize: 18, fontWeight: 700, color: '#fff' }}>
                          <span>Grand Total</span><span>£{rGrandTotal.toFixed(2)}</span>
                        </div>
                      </div>
                    )}

                    {/* 9. Instruction Attachments */}
                    <div style={{ ...sectionStyle, marginBottom: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>
                        Instruction Attachments {ra.length > 0 ? `(${ra.length})` : ''}
                      </div>
                      {ra.length === 0 ? (
                        <p style={{ fontSize: 13, color: '#9ca3af', margin: 0 }}>No attachments on linked instruction.</p>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                          {ra.map(att => (
                            <div key={att.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 6, gap: 12 }}>
                              <div style={{ minWidth: 0 }}>
                                <div style={{ fontSize: 13, fontWeight: 600, color: '#111', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>📄 {att.file_name}</div>
                                {att.file_size && <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{Math.round(att.file_size / 1024)} KB</div>}
                              </div>
                              {att.signedUrl && (
                                <a href={att.signedUrl} target="_blank" rel="noopener noreferrer"
                                  style={{ fontSize: 12, fontWeight: 600, color: '#2563eb', textDecoration: 'none', whiteSpace: 'nowrap', flexShrink: 0 }}>
                                  Download ↓
                                </a>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </>
                );
              })()}
            </div>

            {/* Sticky footer */}
            {!reviewDwModal.loading && (
              <div style={{ padding: '14px 24px', borderTop: '1px solid #e5e7eb', flexShrink: 0, background: '#fff', borderRadius: '0 0 12px 12px' }}>
                <div style={{ background: '#fffbeb', border: '1px solid #f59e0b', borderLeft: '4px solid #f59e0b', borderRadius: 6, padding: '12px 16px', marginBottom: 12 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#92400e', textTransform: 'uppercase', marginBottom: 6 }}>IMPORTANT — BINDING AGREEMENT</div>
                  <div style={{ fontSize: 12, color: '#78350f', lineHeight: 1.6 }}>
                    By clicking "Agree" you confirm that you have reviewed the complete daywork sheet including all labour, materials and plant records, and that the works, hours and resources recorded are accurate and agreed. This action constitutes a formal binding agreement under the terms of the subcontract and will be retained as a contractual record. Once agreed, this daywork sheet cannot be altered without a formal written amendment.
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                  <button onClick={() => setReviewDwModal(null)} style={{ ...btnBase, background: '#f3f4f6', color: '#374151' }}>Close</button>
                  <button
                    onClick={() => setSignModal({ dw: reviewDwModal.dw, disputeMode: true, disputeReason: '', signerName: profile.full_name || '', signerPosition: '', signerCompany: profile.company || '', agreed: false })}
                    style={{ ...btnBase, background: '#fff', color: '#d97706', border: '1px solid #fde68a' }}
                  >Dispute</button>
                  <button
                    onClick={() => setSignModal({ dw: reviewDwModal.dw, disputeMode: false, disputeReason: '', signerName: profile.full_name || '', signerPosition: '', signerCompany: profile.company || '', agreed: false })}
                    style={{ ...btnBase, background: '#16a34a', color: '#fff' }}
                  >Agree</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ─── Sign Daywork Modal ─── */}
      {signModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <div style={{ background: '#fff', borderRadius: 10, maxWidth: 520, width: '100%', padding: 28, maxHeight: '90vh', overflowY: 'auto' }}>
            {signModal.disputeMode ? (
              <>
                <h3 style={{ fontSize: 17, fontWeight: 700, margin: '0 0 6px', color: '#d97706' }}>Dispute Daywork — {signModal.dw.reference}</h3>
                <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 16px' }}>Provide your details and reasons for disputing this daywork sheet.</p>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Your Full Name *</label>
                  <input value={signModal.signerName} onChange={e => setSignModal(m => ({ ...m, signerName: e.target.value }))} style={inputStyle} placeholder="Full name" />
                </div>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Your Position / Role</label>
                  <input value={signModal.signerPosition} onChange={e => setSignModal(m => ({ ...m, signerPosition: e.target.value }))} style={inputStyle} placeholder="e.g. Commercial Manager" />
                </div>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Your Company *</label>
                  <input value={signModal.signerCompany} onChange={e => setSignModal(m => ({ ...m, signerCompany: e.target.value }))} style={inputStyle} placeholder="Company name" />
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Reason for Dispute *</label>
                  <textarea
                    value={signModal.disputeReason}
                    onChange={e => setSignModal(m => ({ ...m, disputeReason: e.target.value }))}
                    style={{ ...inputStyle, minHeight: 100, resize: 'vertical' }}
                    placeholder="Describe specifically which items you are disputing..."
                  />
                </div>
                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                  <button onClick={() => setSignModal(null)} style={{ ...btnBase, background: '#f3f4f6', color: '#374151' }}>Cancel</button>
                  <button onClick={handleSignDaywork} disabled={!signModal.disputeReason.trim() || !signModal.signerName.trim() || !signModal.signerCompany.trim()} style={{ ...btnBase, background: '#d97706', color: '#fff', opacity: (!signModal.disputeReason.trim() || !signModal.signerName.trim() || !signModal.signerCompany.trim()) ? 0.6 : 1 }}>Submit Dispute</button>
                </div>
              </>
            ) : (
              <>
                <h3 style={{ fontSize: 17, fontWeight: 700, margin: '0 0 6px' }}>Sign Daywork — {signModal.dw.reference}</h3>
                <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 16px' }}>
                  Confirm on behalf of your company that you agree to the works, hours, materials and plant recorded for <strong>{fmtDate(signModal.dw.date_of_works)}</strong>.
                </p>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Your Full Name *</label>
                  <input value={signModal.signerName} onChange={e => setSignModal(m => ({ ...m, signerName: e.target.value }))} style={inputStyle} placeholder="Full name" />
                </div>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Your Position / Role</label>
                  <input value={signModal.signerPosition} onChange={e => setSignModal(m => ({ ...m, signerPosition: e.target.value }))} style={inputStyle} placeholder="e.g. Quantity Surveyor" />
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Your Company *</label>
                  <input value={signModal.signerCompany} onChange={e => setSignModal(m => ({ ...m, signerCompany: e.target.value }))} style={inputStyle} placeholder="Company name" />
                </div>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', marginBottom: 20, background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6, padding: '12px 14px' }}>
                  <input type="checkbox" checked={signModal.agreed} onChange={e => setSignModal(m => ({ ...m, agreed: e.target.checked }))} style={{ marginTop: 2, flexShrink: 0 }} />
                  <span style={{ fontSize: 13, color: '#374151', lineHeight: 1.5 }}>
                    I confirm on behalf of <strong>{signModal.signerCompany || 'my company'}</strong> that I agree to the works, hours, materials and plant recorded in this daywork sheet. I understand this constitutes a binding agreement.
                  </span>
                </label>
                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                  <button onClick={() => setSignModal(null)} style={{ ...btnBase, background: '#f3f4f6', color: '#374151' }}>Cancel</button>
                  <button onClick={handleSignDaywork} disabled={!signModal.agreed || !signModal.signerName.trim() || !signModal.signerCompany.trim()} style={{ ...btnBase, background: '#16a34a', color: '#fff', opacity: (!signModal.agreed || !signModal.signerName.trim() || !signModal.signerCompany.trim()) ? 0.6 : 1 }}>Confirm & Sign</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ─── Variation Review Modal ─── */}
      {reviewVarModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: '#fff', borderRadius: 12, maxWidth: 800, width: '100%', maxHeight: '95vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

            {/* Header */}
            <div style={{ background: '#000', padding: '16px 24px', flexShrink: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Variation Quotation — Full Review</div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: '#fff', fontFamily: 'monospace', marginBottom: 2 }}>{reviewVarModal.variation.reference}</div>
                  {reviewVarModal.variation.title && <div style={{ fontSize: 14, color: '#e5e7eb', marginBottom: 6 }}>{reviewVarModal.variation.title}</div>}
                  <div style={{ fontSize: 24, fontWeight: 700, color: '#fff' }}>{fmtCurrency(reviewVarModal.variation.value)}</div>
                </div>
                <button onClick={() => setReviewVarModal(null)} style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.3)', color: '#94a3b8', borderRadius: 6, padding: '6px 14px', fontSize: 13, cursor: 'pointer', marginTop: 4, flexShrink: 0 }}>Close</button>
              </div>
            </div>

            {/* Scrollable body */}
            <div style={{ overflowY: 'auto', flex: 1, padding: '20px 24px', background: '#f3f4f6' }}>
              {reviewVarModal.loading ? (
                <div style={{ textAlign: 'center', padding: 48, color: '#6b7280' }}>Loading document…</div>
              ) : (() => {
                const { variation: rv, attachments: rva } = reviewVarModal;
                const sectionStyle = { background: '#fff', borderRadius: 8, border: '1px solid #e5e7eb', padding: '18px 20px', marginBottom: 16 };
                const secTitle = (t) => <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 12 }}>{t}</div>;
                const infoR = (label, value) => value ? (
                  <div style={{ display: 'flex', gap: 8, marginBottom: 8, fontSize: 14 }}>
                    <span style={{ fontWeight: 600, color: '#6b7280', minWidth: 160 }}>{label}</span>
                    <span style={{ color: '#111', flex: 1 }}>{value}</span>
                  </div>
                ) : null;
                return (
                  <>
                    {/* JCT Warning */}
                    <div style={{ borderLeft: '4px solid #f59e0b', background: '#fffbeb', borderRadius: '0 8px 8px 0', padding: '14px 18px', marginBottom: 16 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Important — Contractual Approval</div>
                      <p style={{ margin: 0, fontSize: 13, color: '#78350f', lineHeight: 1.7 }}>
                        By agreeing to this variation you acknowledge your authority to act on behalf of <strong>{project?.main_contractor || 'your company'}</strong>.
                        An agreement constitutes a <strong>binding obligation</strong> under the terms of the subcontract.
                      </p>
                    </div>

                    {/* Variation Details */}
                    <div style={sectionStyle}>
                      {secTitle('Variation Details')}
                      {infoR('Reference', rv.reference)}
                      {infoR('Title', rv.title)}
                      {infoR('Valuation Basis', fmtValuationBasis(rv.valuation_basis))}
                      {infoR('Instruction', rv.instructions ? `${rv.instructions.reference}${rv.instructions.type ? ` (${rv.instructions.type})` : ''}` : null)}
                    </div>

                    {/* Description */}
                    {rv.description && (
                      <div style={sectionStyle}>
                        {secTitle('Description of Works')}
                        <p style={{ fontSize: 13, color: '#374151', lineHeight: 1.6, margin: 0 }}>{rv.description}</p>
                      </div>
                    )}

                    {/* Scope Notes */}
                    {rv.scope_notes && (
                      <div style={{ borderLeft: '4px solid #f59e0b', background: '#fffbeb', borderRadius: '0 8px 8px 0', padding: '14px 18px', marginBottom: 16 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Reason Outside Subcontract Scope</div>
                        <p style={{ margin: 0, fontSize: 13, color: '#78350f', lineHeight: 1.6 }}>{rv.scope_notes}</p>
                      </div>
                    )}

                    {/* Valuation Summary */}
                    <div style={{ background: '#000', borderRadius: 8, padding: '18px 20px', marginBottom: 16 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 14 }}>Valuation Summary</div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <div style={{ fontSize: 13, color: '#94a3b8' }}>Submitted Value</div>
                          <div style={{ fontSize: 28, fontWeight: 700, color: '#fff', marginTop: 4 }}>{fmtCurrency(rv.value)}</div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: 13, color: '#94a3b8' }}>Valuation Basis</div>
                          <div style={{ fontSize: 15, fontWeight: 600, color: '#e5e7eb', marginTop: 4 }}>{fmtValuationBasis(rv.valuation_basis)}</div>
                        </div>
                      </div>
                    </div>

                    {/* Attachments */}
                    <div style={{ ...sectionStyle, marginBottom: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>
                        Supporting Documents {rva.length > 0 ? `(${rva.length})` : ''}
                      </div>
                      {rva.length === 0 ? (
                        <p style={{ fontSize: 13, color: '#9ca3af', margin: 0 }}>No attachments submitted with this variation.</p>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                          {rva.map(att => (
                            <div key={att.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 6, gap: 12 }}>
                              <div style={{ minWidth: 0 }}>
                                <div style={{ fontSize: 13, fontWeight: 600, color: '#111', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>📄 {att.file_name}</div>
                                {att.file_size && <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{Math.round(att.file_size / 1024)} KB</div>}
                              </div>
                              {att.signedUrl && (
                                <a href={att.signedUrl} target="_blank" rel="noopener noreferrer"
                                  style={{ fontSize: 12, fontWeight: 600, color: '#2563eb', textDecoration: 'none', whiteSpace: 'nowrap', flexShrink: 0 }}>
                                  Download ↓
                                </a>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </>
                );
              })()}
            </div>

            {/* Sticky footer */}
            {!reviewVarModal.loading && (
              <div style={{ padding: '14px 24px', borderTop: '1px solid #e5e7eb', display: 'flex', gap: 10, justifyContent: 'flex-end', flexShrink: 0, background: '#fff', borderRadius: '0 0 12px 12px' }}>
                <button onClick={() => setReviewVarModal(null)} style={{ ...btnBase, background: '#f3f4f6', color: '#374151' }}>Close</button>
                <button
                  onClick={() => setVarModal({ variation: reviewVarModal.variation, responseType: 'disputed', reason: '', agreedValue: '' })}
                  style={{ ...btnBase, background: '#dc2626', color: '#fff' }}
                >Dispute</button>
                <button
                  onClick={() => setVarModal({ variation: reviewVarModal.variation, responseType: 'part_agreed', reason: '', agreedValue: reviewVarModal.variation.value != null ? String(reviewVarModal.variation.value) : '' })}
                  style={{ ...btnBase, background: '#d97706', color: '#fff' }}
                >Part Agree</button>
                <button
                  onClick={() => setVarModal({ variation: reviewVarModal.variation, responseType: 'agreed', reason: '', agreedValue: reviewVarModal.variation.value != null ? String(reviewVarModal.variation.value) : '' })}
                  style={{ ...btnBase, background: '#16a34a', color: '#fff' }}
                >Agree in Full</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ─── Variation Response Modal ─── */}
      {varModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <div style={{ background: '#fff', borderRadius: 10, maxWidth: 520, width: '100%', padding: 28, maxHeight: '90vh', overflowY: 'auto' }}>
            <h3 style={{ fontSize: 17, fontWeight: 700, margin: '0 0 4px' }}>Respond to Variation</h3>
            <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 6px' }}>{varModal.variation.reference}{varModal.variation.title ? ` — ${varModal.variation.title}` : ''}</p>
            <div style={{ fontSize: 20, fontWeight: 700, color: '#111', margin: '0 0 18px' }}>{fmtCurrency(varModal.variation.value)}</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8, marginBottom: 16 }}>
              {varResponseOptions.map(opt => (
                <div key={opt.id} onClick={() => setVarModal(m => ({ ...m, responseType: opt.id }))} style={{ padding: '12px 8px', border: `2px solid ${varModal.responseType === opt.id ? opt.border : '#e5e7eb'}`, borderRadius: 6, cursor: 'pointer', textAlign: 'center', fontWeight: 700, fontSize: 13, color: varModal.responseType === opt.id ? opt.color : '#6b7280', background: varModal.responseType === opt.id ? opt.bg : '#fff' }}>
                  {opt.label}
                </div>
              ))}
            </div>
            {varModal.responseType === 'part_agreed' && (
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Agreed Value (£) *</label>
                <input type="number" step="0.01" value={varModal.agreedValue} onChange={e => setVarModal(m => ({ ...m, agreedValue: e.target.value }))} style={{ ...inputStyle, maxWidth: 180 }} placeholder="0.00" />
              </div>
            )}
            {(varModal.responseType === 'part_agreed' || varModal.responseType === 'disputed') && (
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>{varModal.responseType === 'disputed' ? 'Reasons for Dispute *' : 'Reason *'}</label>
                <textarea value={varModal.reason} onChange={e => setVarModal(m => ({ ...m, reason: e.target.value }))} style={{ ...inputStyle, minHeight: 80 }} placeholder="Provide your reasons..." />
              </div>
            )}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
              <button onClick={() => setVarModal(null)} style={{ ...btnBase, background: '#f3f4f6', color: '#374151' }}>Cancel</button>
              <button
                onClick={handleVarResponse}
                disabled={!varModal.responseType || ((varModal.responseType === 'part_agreed' || varModal.responseType === 'disputed') && !varModal.reason.trim())}
                style={{ ...btnBase, background: varModal.responseType === 'agreed' ? '#16a34a' : varModal.responseType === 'part_agreed' ? '#d97706' : varModal.responseType === 'disputed' ? '#dc2626' : '#9ca3af', color: '#fff' }}
              >
                Confirm Response
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── EWN/CVI Response Modal ─── */}
      {ewnModal && (() => {
        const instr = ewnModal.instr;
        const isEwn = instr.type === 'EWN';
        const submitDisabled = !ewnModal.selectedResponse
          || (ewnModal.selectedResponse === 'query' && !ewnModal.queryText.trim())
          || (ewnModal.selectedResponse === 'disputed' && !ewnModal.disputeText.trim());
        return (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
            <div style={{ background: '#fff', borderRadius: 10, maxWidth: 720, width: '100%', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>

              {/* Modal header */}
              <div style={{ padding: '18px 24px', borderBottom: '1px solid #e5e7eb', flexShrink: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 3, textTransform: 'uppercase', ...(isEwn ? { background: '#fffbeb', color: '#92400e' } : { background: '#dbeafe', color: '#1e40af' }) }}>{instr.type}</span>
                  <span style={{ fontWeight: 700, fontSize: 16 }}>{instr.reference}</span>
                  {instr.date && <span style={{ fontSize: 13, color: '#6b7280' }}>· {fmtDate(instr.date)}</span>}
                </div>
                <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 3 }}>{isEwn ? 'Early Warning Notice' : 'Confirmation of Verbal Instruction'}</div>
              </div>

              {/* Scrollable body */}
              <div style={{ overflowY: 'auto', flex: 1, padding: '20px 24px' }}>

                {/* Section 1 — Notice Details */}
                <div style={{ marginBottom: 22 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 12 }}>Notice Details</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 7, fontSize: 14, marginBottom: 14 }}>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <span style={{ fontWeight: 600, color: '#6b7280', minWidth: 200, flexShrink: 0 }}>Project</span>
                      <span style={{ color: '#111' }}>{project?.name}</span>
                    </div>
                    {isEwn ? (<>
                      {instr.location && (
                        <div style={{ display: 'flex', gap: 8 }}>
                          <span style={{ fontWeight: 600, color: '#6b7280', minWidth: 200, flexShrink: 0 }}>Location</span>
                          <span style={{ color: '#111' }}>{instr.location}{instr.level ? ` · ${instr.level}` : ''}</span>
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: 8 }}>
                        <span style={{ fontWeight: 600, color: '#6b7280', minWidth: 200, flexShrink: 0 }}>Date Noticed</span>
                        <span style={{ color: '#111' }}>{fmtDate(instr.date)}{instr.time ? ` at ${String(instr.time).substring(0, 5)}` : ''}</span>
                      </div>
                      {instr.instructed_by_name && (
                        <div style={{ display: 'flex', gap: 8 }}>
                          <span style={{ fontWeight: 600, color: '#6b7280', minWidth: 200, flexShrink: 0 }}>Raised By</span>
                          <span style={{ color: '#111' }}>{instr.instructed_by_name}</span>
                        </div>
                      )}
                    </>) : (<>
                      {instr.instructed_by_name && (
                        <div style={{ display: 'flex', gap: 8 }}>
                          <span style={{ fontWeight: 600, color: '#6b7280', minWidth: 200, flexShrink: 0 }}>Verbal instruction given by</span>
                          <span style={{ color: '#111' }}>{instr.instructed_by_name}{instr.instructed_by_role ? `, ${instr.instructed_by_role}` : ''}</span>
                        </div>
                      )}
                      {instr.verbal_date && (
                        <div style={{ display: 'flex', gap: 8 }}>
                          <span style={{ fontWeight: 600, color: '#6b7280', minWidth: 200, flexShrink: 0 }}>Date / Time</span>
                          <span style={{ color: '#111' }}>{fmtDate(instr.verbal_date)}{instr.verbal_time ? ` at ${String(instr.verbal_time).substring(0, 5)}` : ''}</span>
                        </div>
                      )}
                      {instr.verbal_method && (
                        <div style={{ display: 'flex', gap: 8 }}>
                          <span style={{ fontWeight: 600, color: '#6b7280', minWidth: 200, flexShrink: 0 }}>Method</span>
                          <span style={{ color: '#111' }}>{instr.verbal_method}</span>
                        </div>
                      )}
                    </>)}
                  </div>

                  {instr.description && (
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5 }}>{isEwn ? 'Issue Description' : 'Works Instructed'}</div>
                      <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 6, padding: '10px 12px', fontSize: 13, color: '#374151', whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>{instr.description}</div>
                    </div>
                  )}

                  {isEwn && instr.potential_impact?.length > 0 && (
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5 }}>Potential Impact</div>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {instr.potential_impact.map((item, i) => (
                          <span key={i} style={{ background: '#fffbeb', color: '#92400e', border: '1px solid #fde68a', borderRadius: 12, padding: '3px 10px', fontSize: 12, fontWeight: 600 }}>{item}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {isEwn && instr.action_requested && (
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5 }}>Action Requested</div>
                      <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 6, padding: '10px 12px', fontSize: 13, color: '#374151', whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>{instr.action_requested}</div>
                    </div>
                  )}
                </div>

                {/* Section 2 — Supporting Documents */}
                <div style={{ marginBottom: 22 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#111', marginBottom: 8 }}>Supporting Documents</div>
                  {ewnAttachmentsLoading ? (
                    <div style={{ fontSize: 13, color: '#9ca3af' }}>Loading documents…</div>
                  ) : ewnAttachments.length === 0 ? (
                    <div style={{ fontSize: 13, color: '#9ca3af' }}>No supporting documents attached.</div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {ewnAttachments.map(att => (
                        <div key={att.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 6, gap: 12 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                            <span style={{ fontSize: 16, flexShrink: 0 }}>📄</span>
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontSize: 13, fontWeight: 600, color: '#111', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{att.file_name}</div>
                              <div style={{ fontSize: 11, color: '#9ca3af' }}>{att.file_size ? `${Math.round(att.file_size / 1024)} KB` : ''}{att.content_type ? ` · ${att.content_type}` : ''}</div>
                            </div>
                          </div>
                          {att.signedUrl && (
                            <a href={att.signedUrl} target="_blank" rel="noreferrer" style={{ ...btnBase, background: '#000', color: '#fff', textDecoration: 'none', flexShrink: 0, fontSize: 12, padding: '6px 14px' }}>
                              Download ↓
                            </a>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Section 3 — Contractual Notice (collapsible) */}
                <div style={{ marginBottom: 22 }}>
                  <button onClick={() => setEwnNoticeExpanded(v => !v)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: '#2563eb', fontWeight: 600, padding: 0 }}>
                    {ewnNoticeExpanded ? '▲ Hide contractual notice' : '▼ Show contractual notice'}
                  </button>
                  {ewnNoticeExpanded && (
                    <div style={{ marginTop: 10, padding: '14px 16px', borderLeft: '4px solid #d97706', background: '#fffbeb', borderRadius: '0 6px 6px 0', fontSize: 13, color: '#374151', lineHeight: 1.7 }}>
                      {isEwn ? (
                        <p style={{ margin: 0 }}><strong>Early Warning Notice</strong> — This notice is issued to warn of a matter which could increase the Defined Cost, delay Completion, or impair the performance of the works in use. The Contractor and Project Manager are required to consider this matter and its potential effects, and either party may instruct the other to attend a risk reduction meeting.</p>
                      ) : (
                        <p style={{ margin: 0 }}><strong>Confirmation of Verbal Instruction</strong> — This document confirms that a verbal instruction was received as described above. Unless written confirmation that this instruction was not given is received within 2 working days, Proline Internals Ltd will proceed with the works and this document will serve as the written record of the instruction received.</p>
                      )}
                    </div>
                  )}
                </div>

                {/* Divider */}
                <hr style={{ border: 'none', borderTop: '1px solid #e5e7eb', margin: '0 0 20px' }} />

                {/* Section 4 — Your Response */}
                <div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: '#111', marginBottom: 14 }}>Your Response</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
                    {ewnResponseCards.map(card => {
                      const sel = ewnModal.selectedResponse === card.id;
                      return (
                        <div key={card.id} onClick={() => setEwnModal(m => ({ ...m, selectedResponse: card.id }))} style={{ padding: '14px 16px', border: `2px solid ${sel ? card.activeBorder : '#e5e7eb'}`, borderRadius: 7, cursor: 'pointer', background: sel ? card.activeBg : '#fff' }}>
                          <div style={{ fontWeight: 700, fontSize: 14, color: sel ? card.activeColor : '#374151' }}>{card.label}</div>
                          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{card.sub}</div>
                        </div>
                      );
                    })}
                  </div>
                  {ewnModal.selectedResponse === 'will_instruct' && (
                    <div style={{ marginBottom: 12 }}>
                      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Expected instruction date: *</label>
                      <input type="date" value={ewnModal.expectedDate} onChange={e => setEwnModal(m => ({ ...m, expectedDate: e.target.value }))} style={{ ...inputStyle, maxWidth: 200 }} />
                      <div style={{ marginTop: 10 }}>
                        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Notes (optional):</label>
                        <textarea value={ewnModal.notes} onChange={e => setEwnModal(m => ({ ...m, notes: e.target.value }))} style={{ ...inputStyle, minHeight: 60 }} placeholder="Any additional comments…" />
                      </div>
                    </div>
                  )}
                  {ewnModal.selectedResponse === 'query' && (
                    <div style={{ marginBottom: 12 }}>
                      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Please describe your query: *</label>
                      <textarea value={ewnModal.queryText} onChange={e => setEwnModal(m => ({ ...m, queryText: e.target.value }))} style={{ ...inputStyle, minHeight: 80 }} placeholder="Describe your query…" />
                    </div>
                  )}
                  {ewnModal.selectedResponse === 'disputed' && (
                    <div style={{ marginBottom: 12 }}>
                      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Please state the grounds for dispute: *</label>
                      <textarea value={ewnModal.disputeText} onChange={e => setEwnModal(m => ({ ...m, disputeText: e.target.value }))} style={{ ...inputStyle, minHeight: 80 }} placeholder="State why you dispute this notice…" />
                    </div>
                  )}
                </div>
              </div>

              {/* Sticky footer */}
              <div style={{ padding: '14px 24px', borderTop: '1px solid #e5e7eb', display: 'flex', gap: 10, justifyContent: 'flex-end', flexShrink: 0, background: '#fff', borderRadius: '0 0 10px 10px' }}>
                <button onClick={() => setEwnModal(null)} style={{ ...btnBase, background: '#f3f4f6', color: '#374151' }}>Cancel</button>
                <button
                  onClick={handleEwnResponse}
                  disabled={submitDisabled}
                  style={{ ...btnBase, background: '#111827', color: '#fff', opacity: submitDisabled ? 0.5 : 1 }}
                >
                  Submit Response
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// ─── MC Portal: Daywork ───────────────────────────────────────────────────────

export function MCPortalDaywork({ dayworkId, token }) {
  const [pageStatus, setPageStatus] = useState('loading');
  const [invalidMsg, setInvalidMsg] = useState('');
  const [invitation, setInvitation] = useState(null);
  const [daywork, setDaywork] = useState(null);
  const [labour, setLabour] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [plant, setPlant] = useState([]);
  const [form, setForm] = useState({ fullName: '', position: '', company: '' });
  const [rates, setRates] = useState(null);
  const [mcInstrAuth, setMcInstrAuth] = useState(null);
  const [instrAttachments, setInstrAttachments] = useState([]);
  const [showChallenge, setShowChallenge] = useState(false);
  const [challengeReason, setChallengeReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [formError, setFormError] = useState('');

  useEffect(() => {
    if (!token || !dayworkId) { setInvalidMsg('Missing token or ID.'); setPageStatus('invalid'); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function load() {
    try {
      // Validate token
      const { data: inv } = await supabase
        .from('mc_invitations')
        .select('*')
        .eq('token', token)
        .eq('item_id', dayworkId)
        .maybeSingle();

      if (!inv) { setInvalidMsg('This link is invalid or has expired. Please contact Proline Internals Ltd.'); setPageStatus('invalid'); return; }
      if (inv.status === 'accepted') { setPageStatus('already_approved'); return; }
      if (inv.status === 'challenged') { setPageStatus('already_challenged'); return; }
      if (inv.status === 'used') { setPageStatus('already_responded'); return; }

      setInvitation(inv);
      setForm({ fullName: inv.full_name || '', position: '', company: inv.company || '' });

      // Load daywork data
      const [{ data: dw }, { data: lab }, { data: mat }, { data: pla }] = await Promise.all([
        supabase.from('dayworks').select('*, projects(id, name, reference, main_contractor), instructions(id, reference, type, description, date, location, level, gridlines, instructed_by_name, instructed_by_role, instruction_method)').eq('id', dayworkId).single(),
        supabase.from('daywork_labour').select('*').eq('daywork_id', dayworkId).order('id'),
        supabase.from('daywork_materials').select('*').eq('daywork_id', dayworkId).order('id'),
        supabase.from('daywork_plant').select('*').eq('daywork_id', dayworkId).order('id'),
      ]);

      if (!dw) { setInvalidMsg('Daywork record not found.'); setPageStatus('invalid'); return; }

      if (['signed', 'disputed'].includes(dw.status)) {
        setDaywork(dw);
        setPageStatus('already_responded');
        return;
      }

      // Phase 2: fetch labour rates and instruction attachments
      const [ratesData, attsRes] = await Promise.all([
        dw.project_id ? fetchLabourRates(dw.project_id).catch(() => null) : Promise.resolve(null),
        dw.instructions?.id
          ? supabase.from('instruction_attachments').select('*').eq('instruction_id', dw.instructions.id).order('created_at')
          : Promise.resolve({ data: [] }),
      ]);

      const attsWithUrls = await Promise.all((attsRes.data || []).map(async att => {
        try {
          const { data: urlData } = await supabase.storage.from('instruction-attachments').createSignedUrl(att.storage_path, 3600);
          return { ...att, signedUrl: urlData?.signedUrl || null };
        } catch { return { ...att, signedUrl: null }; }
      }));

      let parsedMcInstrAuth = null;
      if (dw.mc_instruction_authority) {
        try { parsedMcInstrAuth = JSON.parse(dw.mc_instruction_authority); } catch {}
      }

      setDaywork(dw);
      setLabour(lab || []);
      setMaterials(mat || []);
      setPlant(pla || []);
      setRates(ratesData);
      setMcInstrAuth(parsedMcInstrAuth);
      setInstrAttachments(attsWithUrls);
      setPageStatus('valid');
    } catch (err) {
      setInvalidMsg('An error occurred loading this page: ' + err.message);
      setPageStatus('invalid');
    }
  }

  async function handleApprove() {
    if (!form.fullName.trim()) { setFormError('Please enter your full name.'); return; }
    if (!form.position.trim()) { setFormError('Please enter your position/title.'); return; }
    if (!form.company.trim()) { setFormError('Please enter your company name.'); return; }
    if (!window.confirm('By clicking Confirm, you are entering into a binding contractual agreement. Are you sure you wish to approve this daywork sheet?')) return;

    setProcessing(true);
    setFormError('');
    try {
      // Guard against race condition — check record is still awaiting response
      const { data: current } = await supabase.from('dayworks').select('status').eq('id', dayworkId).single();
      if (current && ['signed', 'disputed'].includes(current.status)) {
        setDaywork(d => ({ ...d, status: current.status }));
        setPageStatus('already_responded');
        return;
      }

      const now = new Date().toISOString();
      await supabase.from('daywork_approvals').insert({
        daywork_id: dayworkId,
        action: 'approved',
        actioned_at: now,
        full_name_confirmed: form.fullName,
        company_confirmed: form.company,
        user_agent: navigator.userAgent,
      });
      await supabase.from('dayworks').update({ status: 'signed', signed_at: now }).eq('id', dayworkId);
      await supabase.from('mc_invitations').update({ status: 'accepted', accepted_at: now }).eq('id', invitation.id);
      await supabase.from('mc_invitations').update({ status: 'used' }).eq('item_id', dayworkId).neq('id', invitation.id);

      // Email MC QS confirmation
      try {
        const { subject, htmlBody } = dayworkAgreementEmail(daywork, daywork.projects, form.fullName, now);
        await sendEmail({ to: invitation.email, subject, htmlBody });
      } catch (e) { console.error('MC confirmation email failed:', e); }

      // Notify Proline
      try {
        const notifyEmails = invitation.notify_emails?.length ? invitation.notify_emails : await getNotifyEmails(daywork.project_id);
        if (notifyEmails.length) {
          const { subject, htmlBody } = dayworkApprovalNotification(daywork, daywork.projects, form.fullName, form.company, now);
          await sendEmail({ to: notifyEmails, subject, htmlBody });
        }
      } catch (e) { console.error('Proline notification email failed:', e); }

      setPageStatus('approved');
    } catch (err) {
      setFormError('An error occurred: ' + err.message);
    } finally {
      setProcessing(false);
    }
  }

  async function handleChallenge() {
    if (!form.fullName.trim()) { setFormError('Please enter your full name.'); return; }
    if (!form.position.trim()) { setFormError('Please enter your position/title.'); return; }
    if (!form.company.trim()) { setFormError('Please enter your company name.'); return; }
    if (challengeReason.trim().length < 50) { setFormError('Please provide detailed reasons (minimum 50 characters).'); return; }

    setProcessing(true);
    setFormError('');
    try {
      const { data: current } = await supabase.from('dayworks').select('status').eq('id', dayworkId).single();
      if (current && ['signed', 'disputed'].includes(current.status)) {
        setDaywork(d => ({ ...d, status: current.status }));
        setPageStatus('already_responded');
        return;
      }

      const now = new Date().toISOString();
      await supabase.from('daywork_approvals').insert({
        daywork_id: dayworkId,
        action: 'challenged',
        actioned_at: now,
        challenge_reason: challengeReason,
        full_name_confirmed: form.fullName,
        company_confirmed: form.company,
        user_agent: navigator.userAgent,
      });
      await supabase.from('dayworks').update({ status: 'disputed', dispute_reason: challengeReason }).eq('id', dayworkId);
      await supabase.from('mc_invitations').update({ status: 'challenged', accepted_at: now }).eq('id', invitation.id);
      await supabase.from('mc_invitations').update({ status: 'used' }).eq('item_id', dayworkId).neq('id', invitation.id);

      // Notify Proline
      try {
        const notifyEmails = invitation.notify_emails?.length ? invitation.notify_emails : await getNotifyEmails(daywork.project_id);
        if (notifyEmails.length) {
          const { subject, htmlBody } = dayworkChallengeNotification(daywork, daywork.projects, challengeReason, form.fullName, form.company);
          await sendEmail({ to: notifyEmails, subject, htmlBody });
        }
      } catch (e) { console.error('Proline challenge notification failed:', e); }

      setPageStatus('challenged');
    } catch (err) {
      setFormError('An error occurred: ' + err.message);
    } finally {
      setProcessing(false);
    }
  }

  if (pageStatus === 'loading') return <PortalShell><Card style={{ textAlign: 'center', padding: '40px' }}><p style={{ color: '#6b7280' }}>Loading…</p></Card></PortalShell>;
  if (pageStatus === 'invalid') return <InvalidPage message={invalidMsg} />;
  if (pageStatus === 'already_responded') return <AlreadyRespondedPage recordType="Daywork Sheet" reference={daywork?.reference} />;
  if (pageStatus === 'already_approved') return <SuccessPage title="Already Approved" lines={['This daywork sheet has already been approved.', 'No further action is required.']} />;
  if (pageStatus === 'already_challenged') return <ChallengedPage lines={['Your challenge for this daywork sheet has already been recorded.', 'Proline Internals Ltd will be in contact.']} />;
  if (pageStatus === 'approved') return <SuccessPage title="Daywork Approved" lines={[`Thank you, ${form.fullName}.`, 'Your approval has been recorded as a binding agreement.', 'Proline Internals Ltd has been notified.']} email={invitation?.email} />;
  if (pageStatus === 'challenged') return <ChallengedPage lines={['Your challenge has been recorded and sent to Proline Internals Ltd.', 'They will review and respond shortly.']} />;

  const totalHours = labour.reduce((s, r) => s + (parseFloat(r.hours_calculated) || calcHours(r.start_time, r.finish_time)), 0);
  const totalMats = materials.reduce((s, r) => s + (parseFloat(r.quantity) || 0) * (parseFloat(r.price_per_unit) || 0), 0);
  const totalPlant = plant.reduce((s, r) => s + (parseFloat(r.quantity) || 0) * (parseFloat(r.rate) || 0), 0);
  const labCost = rates ? labour.reduce((s, r) => s + (parseFloat(r.hours_calculated) || calcHours(r.start_time, r.finish_time)) * rates.getRateForTrade(r.trade), 0) : null;
  const matCostTotal = rates ? totalMats * (1 + rates.materialsUplift / 100) : null;
  const plantCostTotal = rates ? totalPlant * (1 + rates.plantUplift / 100) : null;
  const grandTotal = (labCost != null && matCostTotal != null && plantCostTotal != null) ? labCost + matCostTotal + plantCostTotal : null;

  return (
    <PortalShell>
      {/* Title */}
      <Card style={{ background: NAVY, border: 'none', padding: '24px 28px', marginBottom: 20 }}>
        <div style={{ color: '#94a3b8', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
          Daywork Sheet for Review and Approval
        </div>
        <div style={{ fontSize: 26, fontWeight: 700, color: '#fff', fontFamily: 'monospace', marginBottom: 4 }}>
          {daywork.reference}
        </div>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 13, color: '#cbd5e1' }}>
          <span>{fmtDate(daywork.date_of_works)}</span>
          <span>·</span>
          <span>{daywork.projects?.reference} — {daywork.projects?.name}</span>
          {daywork.projects?.main_contractor && <><span>·</span><span>{daywork.projects.main_contractor}</span></>}
        </div>
      </Card>

      {/* JCT Notice */}
      <Card style={{ background: '#fffbeb', border: '1px solid #f59e0b' }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <div style={{ fontSize: 20, flexShrink: 0 }}>⚠️</div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
              IMPORTANT — CONTRACTUAL APPROVAL
            </div>
            <p style={{ margin: 0, fontSize: 13, color: '#78350f', lineHeight: 1.7 }}>
              By approving this daywork sheet you confirm on behalf of{' '}
              <strong>{daywork.projects?.main_contractor || 'your company'}</strong>{' '}
              that you agree to the hours, materials and plant recorded below. This approval constitutes a{' '}
              <strong>binding agreement</strong> under the terms of the subcontract between{' '}
              {daywork.projects?.main_contractor || 'your company'} and Proline Internals Ltd. You confirm that you
              are duly authorised to make this agreement on behalf of your company. Once approved, this record
              cannot be amended without written agreement from both parties.
            </p>
          </div>
        </div>
      </Card>

      {/* Details */}
      <Card>
        <SectionTitle>Daywork Details</SectionTitle>
        <InfoRow label="Project" value={`${daywork.projects?.reference} — ${daywork.projects?.name}`} />
        <InfoRow label="Date of Works" value={fmtDate(daywork.date_of_works)} />
        {daywork.instructions?.reference && <InfoRow label="Instruction Ref" value={`${daywork.instructions.reference} (${daywork.instructions.type})`} />}
        {daywork.location && <InfoRow label="Location" value={daywork.location} />}
        {daywork.gridlines && <InfoRow label="Gridlines" value={daywork.gridlines} />}
        {daywork.level && <InfoRow label="Level / Floor" value={daywork.level} />}
        {daywork.description && (
          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>Description of Works</div>
            <p style={{ fontSize: 13, color: '#374151', lineHeight: 1.6, margin: 0 }}>{daywork.description}</p>
          </div>
        )}
      </Card>

      {/* Authority for These Works */}
      {mcInstrAuth && (
        <Card>
          <SectionTitle>Authority for These Works</SectionTitle>
          {[
            ['Reference', mcInstrAuth.reference],
            ['Type', mcInstrAuth.type],
            ['Date', mcInstrAuth.date ? fmtDate(mcInstrAuth.date) : null],
            ['Time', mcInstrAuth.time],
            ['Instructed By', [mcInstrAuth.instructed_by_name, mcInstrAuth.instructed_by_role].filter(Boolean).join(' — ') || null],
            ['Method', mcInstrAuth.instruction_method],
            ['Main Contractor', mcInstrAuth.main_contractor],
          ].filter(([, v]) => v).map(([label, value]) => (
            <InfoRow key={label} label={label} value={value} />
          ))}
          {mcInstrAuth.description && (
            <div style={{ marginTop: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>Authority Description</div>
              <p style={{ fontSize: 13, color: '#374151', lineHeight: 1.6, margin: 0 }}>{mcInstrAuth.description}</p>
            </div>
          )}
        </Card>
      )}

      {/* Description of Works Carried Out */}
      {daywork.works_description && (
        <Card>
          <SectionTitle>Description of Works Carried Out</SectionTitle>
          <p style={{ fontSize: 13, color: '#374151', lineHeight: 1.6, margin: 0 }}>{daywork.works_description}</p>
        </Card>
      )}

      {/* Reason Outside Subcontract Scope */}
      {daywork.reason_outside_scope && (
        <div style={{ background: '#fffbeb', borderLeft: '4px solid #f59e0b', borderRadius: '0 8px 8px 0', padding: '14px 18px', marginBottom: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Reason Works are Outside Subcontract Scope</div>
          <p style={{ margin: 0, fontSize: 13, color: '#78350f', lineHeight: 1.6 }}>{daywork.reason_outside_scope}</p>
        </div>
      )}

      {/* Labour */}
      {labour.length > 0 && (
        <Card>
          <SectionTitle>Labour ({totalHours.toFixed(2)} hrs total)</SectionTitle>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f9fafb' }}>
                  {['Operative', 'Trade', 'Start', 'Finish', 'Hours', 'Rate (£/h)', 'Cost (£)', 'Activity'].map(h => (
                    <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {labour.map((r, i) => {
                  const hrs = parseFloat(r.hours_calculated) || calcHours(r.start_time, r.finish_time);
                  const rate = rates ? rates.getRateForTrade(r.trade) : null;
                  const cost = rate != null ? hrs * rate : null;
                  return (
                    <tr key={r.id} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                      <td style={{ padding: '8px 10px', fontWeight: 600, borderBottom: '1px solid #f3f4f6' }}>{r.operative_name || '—'}</td>
                      <td style={{ padding: '8px 10px', borderBottom: '1px solid #f3f4f6' }}>{r.trade || '—'}</td>
                      <td style={{ padding: '8px 10px', borderBottom: '1px solid #f3f4f6', fontFamily: 'monospace' }}>{(r.start_time || '').substring(0, 5) || '—'}</td>
                      <td style={{ padding: '8px 10px', borderBottom: '1px solid #f3f4f6', fontFamily: 'monospace' }}>{(r.finish_time || '').substring(0, 5) || '—'}</td>
                      <td style={{ padding: '8px 10px', fontWeight: 700, color: '#16a34a', borderBottom: '1px solid #f3f4f6' }}>{hrs > 0 ? `${hrs.toFixed(2)}h` : '—'}</td>
                      <td style={{ padding: '8px 10px', borderBottom: '1px solid #f3f4f6' }}>{rate != null ? `£${rate.toFixed(2)}` : '—'}</td>
                      <td style={{ padding: '8px 10px', fontWeight: 600, borderBottom: '1px solid #f3f4f6' }}>{cost != null ? `£${cost.toFixed(2)}` : '—'}</td>
                      <td style={{ padding: '8px 10px', color: '#6b7280', borderBottom: '1px solid #f3f4f6' }}>{r.activity_note || '—'}</td>
                    </tr>
                  );
                })}
                <tr style={{ background: '#f0fdf4' }}>
                  <td colSpan={4} style={{ padding: '8px 10px', fontWeight: 700, color: '#16a34a' }}>Total Labour</td>
                  <td style={{ padding: '8px 10px', fontWeight: 700, color: '#16a34a' }}>{totalHours.toFixed(2)}h</td>
                  <td />
                  <td style={{ padding: '8px 10px', fontWeight: 700, color: '#16a34a' }}>{labCost != null ? `£${labCost.toFixed(2)}` : '—'}</td>
                  <td />
                </tr>
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Materials */}
      {materials.length > 0 && (
        <Card>
          <SectionTitle>Materials</SectionTitle>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f9fafb' }}>
                  {['Description', 'Qty', 'Unit', 'Rate (£)', 'Total (£)'].map(h => (
                    <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {materials.map((r, i) => {
                  const total = (parseFloat(r.quantity) || 0) * (parseFloat(r.price_per_unit) || 0);
                  return (
                    <tr key={r.id} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                      <td style={{ padding: '8px 10px', borderBottom: '1px solid #f3f4f6' }}>{r.description}</td>
                      <td style={{ padding: '8px 10px', borderBottom: '1px solid #f3f4f6' }}>{r.quantity}</td>
                      <td style={{ padding: '8px 10px', borderBottom: '1px solid #f3f4f6' }}>{r.unit || '—'}</td>
                      <td style={{ padding: '8px 10px', borderBottom: '1px solid #f3f4f6' }}>£{parseFloat(r.price_per_unit || 0).toFixed(2)}</td>
                      <td style={{ padding: '8px 10px', fontWeight: 600, borderBottom: '1px solid #f3f4f6' }}>£{total.toFixed(2)}</td>
                    </tr>
                  );
                })}
                <tr style={{ background: '#f0fdf4' }}>
                  <td colSpan={4} style={{ padding: '8px 10px', fontWeight: 700, color: '#16a34a' }}>Total Materials</td>
                  <td style={{ padding: '8px 10px', fontWeight: 700, color: '#16a34a' }}>£{totalMats.toFixed(2)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Plant */}
      {plant.length > 0 && (
        <Card>
          <SectionTitle>Plant & Equipment</SectionTitle>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f9fafb' }}>
                  {['Description', 'Qty', 'Unit', 'Rate (£)', 'Total (£)'].map(h => (
                    <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {plant.map((r, i) => {
                  const total = (parseFloat(r.quantity) || 0) * (parseFloat(r.rate) || 0);
                  return (
                    <tr key={r.id} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                      <td style={{ padding: '8px 10px', borderBottom: '1px solid #f3f4f6' }}>{r.description}</td>
                      <td style={{ padding: '8px 10px', borderBottom: '1px solid #f3f4f6' }}>{r.quantity}</td>
                      <td style={{ padding: '8px 10px', borderBottom: '1px solid #f3f4f6' }}>{r.unit || '—'}</td>
                      <td style={{ padding: '8px 10px', borderBottom: '1px solid #f3f4f6' }}>£{parseFloat(r.rate || 0).toFixed(2)}</td>
                      <td style={{ padding: '8px 10px', fontWeight: 600, borderBottom: '1px solid #f3f4f6' }}>£{total.toFixed(2)}</td>
                    </tr>
                  );
                })}
                <tr style={{ background: '#f0fdf4' }}>
                  <td colSpan={4} style={{ padding: '8px 10px', fontWeight: 700, color: '#16a34a' }}>Total Plant</td>
                  <td style={{ padding: '8px 10px', fontWeight: 700, color: '#16a34a' }}>£{(plantCostTotal != null ? plantCostTotal : totalPlant).toFixed(2)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Cost Summary */}
      {grandTotal != null && (
        <Card style={{ background: '#000000', border: 'none' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 14 }}>Cost Summary</div>
          {[
            ['Labour', labCost],
            [`Materials${rates?.materialsUplift ? ` (incl. ${rates.materialsUplift}% uplift)` : ''}`, matCostTotal],
            [`Plant${rates?.plantUplift ? ` (incl. ${rates.plantUplift}% uplift)` : ''}`, plantCostTotal],
          ].map(([label, val]) => (
            <div key={label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, color: '#e5e7eb', marginBottom: 8 }}>
              <span>{label}</span>
              <span>{val != null ? `£${val.toFixed(2)}` : '—'}</span>
            </div>
          ))}
          <div style={{ borderTop: '1px solid rgba(255,255,255,0.2)', marginTop: 8, paddingTop: 10, display: 'flex', justifyContent: 'space-between', fontSize: 18, fontWeight: 700, color: '#fff' }}>
            <span>Grand Total</span>
            <span>£{grandTotal.toFixed(2)}</span>
          </div>
        </Card>
      )}

      {/* Instruction Attachments */}
      {instrAttachments.length > 0 && (
        <Card>
          <SectionTitle>Instruction Attachments ({instrAttachments.length})</SectionTitle>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {instrAttachments.map(att => (
              <div key={att.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 6, gap: 12 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#111', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>📄 {att.file_name}</div>
                  {att.file_size && <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{Math.round(att.file_size / 1024)} KB</div>}
                </div>
                {att.signedUrl && (
                  <a href={att.signedUrl} target="_blank" rel="noopener noreferrer"
                    style={{ fontSize: 12, fontWeight: 600, color: '#2563eb', textDecoration: 'none', whiteSpace: 'nowrap', flexShrink: 0 }}>
                    Download ↓
                  </a>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Submission Details */}
      {(daywork.submitted_to_name || daywork.submitted_at) && (
        <Card>
          <SectionTitle>Submission Details</SectionTitle>
          {daywork.submitted_to_name && <InfoRow label="Submitted To" value={daywork.submitted_to_name} />}
          {daywork.submitted_to_company && <InfoRow label="MC Company" value={daywork.submitted_to_company} />}
          {daywork.submitted_at && <InfoRow label="Date Submitted" value={fmtDT(daywork.submitted_at)} />}
        </Card>
      )}

      {/* Action section */}
      <Card style={{ border: '2px solid #e5e7eb' }}>
        <SectionTitle>Your Details & Response</SectionTitle>
        <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 20, marginTop: 0 }}>
          Please confirm your details before approving or challenging this daywork sheet.
          These will be recorded as part of the formal agreement.
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12, marginBottom: 20 }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Full Name *</label>
            <input
              value={form.fullName}
              onChange={e => setForm(f => ({ ...f, fullName: e.target.value }))}
              style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' }}
              placeholder="Your full name"
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Position / Title *</label>
            <input
              value={form.position}
              onChange={e => setForm(f => ({ ...f, position: e.target.value }))}
              style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' }}
              placeholder="e.g. Site Manager, QS"
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Company *</label>
            <input
              value={form.company}
              onChange={e => setForm(f => ({ ...f, company: e.target.value }))}
              style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' }}
              placeholder="Your company name"
            />
          </div>
        </div>

        {formError && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#dc2626' }}>
            {formError}
          </div>
        )}

        <div style={{ background: '#fffbeb', border: '1px solid #f59e0b', borderLeft: '4px solid #f59e0b', borderRadius: 6, padding: '12px 16px', marginBottom: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#92400e', textTransform: 'uppercase', marginBottom: 6 }}>IMPORTANT — BINDING AGREEMENT</div>
          <div style={{ fontSize: 12, color: '#78350f', lineHeight: 1.6 }}>
            By clicking "Approve Daywork Sheet" you confirm that you have reviewed the complete daywork sheet including all labour, materials and plant records, and that the works, hours and resources recorded are accurate and agreed. This action constitutes a formal binding agreement under the terms of the subcontract and will be retained as a contractual record. Once approved, this daywork sheet cannot be altered without a formal written amendment.
          </div>
        </div>
        {!showChallenge ? (
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <button
              onClick={handleApprove}
              disabled={processing}
              style={{
                padding: '12px 28px', background: '#16a34a', color: '#fff',
                border: 'none', borderRadius: 6, fontSize: 15, fontWeight: 700,
                cursor: processing ? 'not-allowed' : 'pointer',
                opacity: processing ? 0.7 : 1, flex: 1, minWidth: 180,
              }}
            >
              {processing ? 'Processing…' : '✅ Approve Daywork Sheet'}
            </button>
            <button
              onClick={() => { setShowChallenge(true); setFormError(''); }}
              disabled={processing}
              style={{
                padding: '12px 28px', background: '#fff', color: '#d97706',
                border: '2px solid #f59e0b', borderRadius: 6, fontSize: 15, fontWeight: 700,
                cursor: processing ? 'not-allowed' : 'pointer',
                opacity: processing ? 0.7 : 1, flex: 1, minWidth: 180,
              }}
            >
              ⚠️ Challenge / Dispute
            </button>
          </div>
        ) : (
          <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: 20 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#92400e', marginBottom: 8 }}>
              Challenge Reasons
            </div>
            <p style={{ fontSize: 13, color: '#78350f', marginBottom: 12 }}>
              Please provide detailed reasons for your challenge. Be specific about which hours, materials or
              plant items you are disputing. Minimum 50 characters required.
            </p>
            <textarea
              value={challengeReason}
              onChange={e => setChallengeReason(e.target.value)}
              style={{ width: '100%', padding: '10px 12px', border: '1px solid #fde68a', borderRadius: 6, fontSize: 13, minHeight: 120, boxSizing: 'border-box', marginBottom: 12 }}
              placeholder="Describe specifically which items you are challenging and why..."
            />
            <div style={{ fontSize: 12, color: '#92400e', marginBottom: 12 }}>
              {challengeReason.trim().length} characters {challengeReason.trim().length < 50 ? `(${50 - challengeReason.trim().length} more required)` : '✓'}
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={handleChallenge}
                disabled={processing || challengeReason.trim().length < 50}
                style={{
                  padding: '10px 24px', background: '#d97706', color: '#fff',
                  border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 700,
                  cursor: (processing || challengeReason.trim().length < 50) ? 'not-allowed' : 'pointer',
                  opacity: (processing || challengeReason.trim().length < 50) ? 0.6 : 1,
                }}
              >
                {processing ? 'Submitting…' : 'Submit Challenge'}
              </button>
              <button
                onClick={() => { setShowChallenge(false); setFormError(''); }}
                style={{ padding: '10px 20px', background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, cursor: 'pointer' }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </Card>
    </PortalShell>
  );
}

// ─── MC Portal: Variation ─────────────────────────────────────────────────────

export function MCPortalVariation({ variationId, token }) {
  const [pageStatus, setPageStatus] = useState('loading');
  const [invalidMsg, setInvalidMsg] = useState('');
  const [invitation, setInvitation] = useState(null);
  const [variation, setVariation] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [form, setForm] = useState({ fullName: '', position: '', company: '' });
  const [responseType, setResponseType] = useState(null); // null | 'agreed' | 'part_agreed' | 'disputed'
  const [agreedValue, setAgreedValue] = useState('');
  const [responseReason, setResponseReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [formError, setFormError] = useState('');

  useEffect(() => {
    if (!token || !variationId) { setInvalidMsg('Missing token or ID.'); setPageStatus('invalid'); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function load() {
    try {
      const { data: inv } = await supabase
        .from('mc_invitations')
        .select('*')
        .eq('token', token)
        .eq('item_id', variationId)
        .maybeSingle();

      if (!inv) { setInvalidMsg('This link is invalid or has expired. Please contact Proline Internals Ltd.'); setPageStatus('invalid'); return; }
      if (inv.status === 'accepted') { setPageStatus('already_responded'); return; }
      if (inv.status === 'used') { setPageStatus('already_responded'); return; }

      setInvitation(inv);
      setForm({ fullName: inv.full_name || '', position: '', company: inv.company || '' });

      const [{ data: v }, { data: atts }] = await Promise.all([
        supabase.from('variations').select('*, projects(id, name, reference, main_contractor), instructions(id, reference, type, description)').eq('id', variationId).single(),
        supabase.from('variation_attachments').select('*').eq('variation_id', variationId).order('uploaded_at'),
      ]);

      if (!v) { setInvalidMsg('Variation record not found.'); setPageStatus('invalid'); return; }

      if (['agreed', 'part_agreed', 'disputed'].includes(v.status)) {
        setVariation(v);
        setPageStatus('already_responded');
        return;
      }

      const attsWithUrls = await Promise.all((atts || []).map(async att => {
        try {
          const { data: urlData } = await supabase.storage.from('variation-attachments').createSignedUrl(att.storage_path, 3600);
          return { ...att, signedUrl: urlData?.signedUrl || null };
        } catch { return { ...att, signedUrl: null }; }
      }));

      setVariation(v);
      setAttachments(attsWithUrls);
      setAgreedValue(v.value ? String(v.value) : '');
      setPageStatus('valid');
    } catch (err) {
      setInvalidMsg('An error occurred: ' + err.message);
      setPageStatus('invalid');
    }
  }

  async function handleSubmitResponse() {
    if (!form.fullName.trim()) { setFormError('Please enter your full name.'); return; }
    if (!form.position.trim()) { setFormError('Please enter your position/title.'); return; }
    if (!form.company.trim()) { setFormError('Please enter your company name.'); return; }
    if (!responseType) { setFormError('Please select a response option.'); return; }
    if (responseType === 'part_agreed' && (!agreedValue || parseFloat(agreedValue) <= 0)) {
      setFormError('Please enter the agreed value for a part-agreed response.');
      return;
    }
    if ((responseType === 'disputed' || responseType === 'part_agreed') && responseReason.trim().length < 50) {
      setFormError('Please provide detailed reasons (minimum 50 characters).');
      return;
    }

    const confirmMsg = responseType === 'agreed'
      ? 'By clicking Confirm, you are agreeing to this variation. This constitutes a binding agreement. Proceed?'
      : responseType === 'part_agreed'
      ? `By clicking Confirm, you are agreeing to a value of £${parseFloat(agreedValue).toFixed(2)}. This is binding. Proceed?`
      : 'Submit your dispute to Proline Internals Ltd?';
    if (!window.confirm(confirmMsg)) return;

    setProcessing(true);
    setFormError('');
    try {
      const { data: current } = await supabase.from('variations').select('status').eq('id', variationId).single();
      if (current && ['agreed', 'part_agreed', 'disputed'].includes(current.status)) {
        setVariation(v => ({ ...v, status: current.status }));
        setPageStatus('already_responded');
        return;
      }

      const now = new Date().toISOString();
      const finalAgreedValue = responseType === 'agreed' ? variation.value : responseType === 'part_agreed' ? parseFloat(agreedValue) : null;

      await supabase.from('variation_approvals').insert({
        variation_id: variationId,
        revision_number: variation.current_revision || 0,
        action: responseType,
        actioned_at: now,
        agreed_value: finalAgreedValue,
        response_reason: responseReason || null,
        full_name_confirmed: form.fullName,
        company_confirmed: form.company,
        user_agent: navigator.userAgent,
      });

      await supabase.from('variations').update({
        status: responseType,
        mc_response: responseType,
        mc_response_at: now,
        mc_response_value: finalAgreedValue,
        mc_response_reason: responseReason || null,
        mc_responded_at: now,
      }).eq('id', variationId);

      await supabase.from('mc_invitations').update({ status: 'accepted', accepted_at: now }).eq('id', invitation.id);
      await supabase.from('mc_invitations').update({ status: 'used' }).eq('item_id', variationId).neq('id', invitation.id);

      // Email MC QS confirmation
      try {
        const { subject, htmlBody } = variationAgreementEmail(variation, variation.projects, form.fullName, now, finalAgreedValue);
        await sendEmail({ to: invitation.email, subject, htmlBody });
      } catch (e) { console.error('MC confirmation email failed:', e); }

      // Notify Proline
      try {
        const notifyEmails = invitation.notify_emails?.length ? invitation.notify_emails : await getNotifyEmails(variation.project_id);
        if (notifyEmails.length) {
          const { subject, htmlBody } = variationResponseNotification(variation, variation.projects, responseType, form.fullName, form.company, responseReason, finalAgreedValue);
          await sendEmail({ to: notifyEmails, subject, htmlBody });
        }
      } catch (e) { console.error('Proline notification failed:', e); }

      setPageStatus(responseType === 'disputed' ? 'disputed' : 'responded');
    } catch (err) {
      setFormError('An error occurred: ' + err.message);
    } finally {
      setProcessing(false);
    }
  }

  if (pageStatus === 'loading') return <PortalShell><Card style={{ textAlign: 'center', padding: '40px' }}><p style={{ color: '#6b7280' }}>Loading…</p></Card></PortalShell>;
  if (pageStatus === 'invalid') return <InvalidPage message={invalidMsg} />;
  if (pageStatus === 'already_responded') return <AlreadyRespondedPage recordType="Variation Quotation" reference={variation?.reference} />;
  if (pageStatus === 'responded') return <SuccessPage title="Response Recorded" lines={[`Thank you, ${form.fullName}.`, 'Your response has been recorded and Proline Internals Ltd has been notified.']} email={invitation?.email} />;
  if (pageStatus === 'disputed') return <ChallengedPage lines={['Your dispute has been recorded and sent to Proline Internals Ltd.', 'They will review and respond with a revised submission.']} />;

  const responseOptions = [
    { id: 'agreed', label: '✅ Agree in Full', color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0' },
    { id: 'part_agreed', label: '~ Part Agree', color: '#d97706', bg: '#fffbeb', border: '#fde68a' },
    { id: 'disputed', label: '✗ Dispute', color: '#dc2626', bg: '#fef2f2', border: '#fecaca' },
  ];

  return (
    <PortalShell>
      {/* Header */}
      <Card style={{ background: NAVY, border: 'none', padding: '24px 28px' }}>
        <div style={{ color: '#94a3b8', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
          Variation Quotation for Review
        </div>
        <div style={{ fontSize: 26, fontWeight: 700, color: '#fff', fontFamily: 'monospace', marginBottom: 4 }}>
          {variation.reference}
        </div>
        <div style={{ fontSize: 16, color: '#e5e7eb', marginBottom: 8 }}>{variation.title}</div>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 13, color: '#cbd5e1', alignItems: 'center' }}>
          <span>{variation.projects?.reference} — {variation.projects?.name}</span>
          {variation.projects?.main_contractor && <><span>·</span><span>{variation.projects.main_contractor}</span></>}
        </div>
        <div style={{ fontSize: 32, fontWeight: 700, color: '#fff', marginTop: 16 }}>
          {fmtCurrency(variation.value)}
        </div>
        <div style={{ fontSize: 12, color: '#94a3b8' }}>{fmtValuationBasis(variation.valuation_basis)}</div>
      </Card>

      {/* JCT Notice */}
      <Card style={{ background: '#fffbeb', border: '1px solid #f59e0b' }}>
        <div style={{ display: 'flex', gap: 12 }}>
          <div style={{ fontSize: 20, flexShrink: 0 }}>⚠️</div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
              IMPORTANT — CONTRACTUAL APPROVAL
            </div>
            <p style={{ margin: 0, fontSize: 13, color: '#78350f', lineHeight: 1.7 }}>
              By responding to this variation quotation you acknowledge your authority to act on behalf of{' '}
              <strong>{variation.projects?.main_contractor || 'your company'}</strong>.
              An "Agree in Full" or "Part Agree" response constitutes a <strong>binding agreement</strong> under
              the terms of the subcontract. You confirm that you are duly authorised to make this
              agreement on behalf of your company.
            </p>
          </div>
        </div>
      </Card>

      {/* Variation details */}
      <Card>
        <SectionTitle>Variation Details</SectionTitle>
        <InfoRow label="Reference" value={variation.reference} />
        <InfoRow label="Title" value={variation.title} />
        <InfoRow label="Project" value={`${variation.projects?.reference} — ${variation.projects?.name}`} />
        <InfoRow label="Valuation Basis" value={fmtValuationBasis(variation.valuation_basis)} />
        <InfoRow label="Submitted Value" value={fmtCurrency(variation.value)} />
        {variation.current_revision > 0 && <InfoRow label="Revision" value={`Rev ${variation.current_revision}`} />}
        {variation.notification_date && <InfoRow label="Notified" value={fmtDate(variation.notification_date)} />}
        {variation.description && (
          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>Description of Works</div>
            <p style={{ fontSize: 13, color: '#374151', lineHeight: 1.6, margin: 0 }}>{variation.description}</p>
          </div>
        )}
        {variation.scope_notes && (
          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>Scope Notes</div>
            <p style={{ fontSize: 13, color: '#374151', lineHeight: 1.6, margin: 0 }}>{variation.scope_notes}</p>
          </div>
        )}
      </Card>

      {/* Linked instruction */}
      {variation.instructions && (
        <Card>
          <SectionTitle>Linked Instruction</SectionTitle>
          <InfoRow label="Reference" value={`${variation.instructions.reference} (${variation.instructions.type})`} />
          {variation.instructions.description && <InfoRow label="Description" value={variation.instructions.description.substring(0, 200)} />}
        </Card>
      )}

      {/* Documents */}
      {attachments.length > 0 && (
        <Card>
          <SectionTitle>Supporting Documents ({attachments.length})</SectionTitle>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {attachments.map(att => (
              <div key={att.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '10px 12px', background: '#f9fafb', borderRadius: 6, border: '1px solid #e5e7eb' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                  <div style={{ width: 36, height: 36, borderRadius: 6, background: '#dbeafe', color: '#1e40af', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, flexShrink: 0 }}>
                    {att.file_type === 'application/pdf' ? 'PDF' : att.file_type?.startsWith('image/') ? 'IMG' : 'DOC'}
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, color: '#111', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{att.file_name}</div>
                    <div style={{ fontSize: 11, color: '#9ca3af' }}>{att.attachment_type?.replace(/_/g, ' ')}</div>
                  </div>
                </div>
                {att.signedUrl && (
                  <a href={att.signedUrl} target="_blank" rel="noopener noreferrer"
                    style={{ fontSize: 12, fontWeight: 600, color: '#2563eb', textDecoration: 'none', whiteSpace: 'nowrap', flexShrink: 0 }}>
                    Download ↓
                  </a>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Action section */}
      <Card style={{ border: '2px solid #e5e7eb' }}>
        <SectionTitle>Your Details & Response</SectionTitle>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12, marginBottom: 20 }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Full Name *</label>
            <input value={form.fullName} onChange={e => setForm(f => ({ ...f, fullName: e.target.value }))}
              style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' }} placeholder="Your full name" />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Position / Title *</label>
            <input value={form.position} onChange={e => setForm(f => ({ ...f, position: e.target.value }))}
              style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' }} placeholder="e.g. QS, Contract Manager" />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Company *</label>
            <input value={form.company} onChange={e => setForm(f => ({ ...f, company: e.target.value }))}
              style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' }} placeholder="Your company name" />
          </div>
        </div>

        {/* Response type selector */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 10 }}>Select your response:</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
            {responseOptions.map(opt => (
              <div
                key={opt.id}
                onClick={() => { setResponseType(opt.id); setFormError(''); }}
                style={{
                  padding: '14px 12px', border: `2px solid ${responseType === opt.id ? opt.border : '#e5e7eb'}`,
                  borderRadius: 6, cursor: 'pointer', textAlign: 'center',
                  fontWeight: 700, fontSize: 14, color: responseType === opt.id ? opt.color : '#6b7280',
                  background: responseType === opt.id ? opt.bg : '#fff',
                  transition: 'all 0.1s',
                }}
              >
                {opt.label}
              </div>
            ))}
          </div>
        </div>

        {/* Part-agree: counter value */}
        {responseType === 'part_agreed' && (
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Agreed Value (£) *</label>
            <div style={{ position: 'relative', maxWidth: 200 }}>
              <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#6b7280', fontWeight: 600 }}>£</span>
              <input
                type="number" step="0.01" min="0"
                value={agreedValue}
                onChange={e => setAgreedValue(e.target.value)}
                style={{ width: '100%', padding: '8px 10px 8px 24px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' }}
                placeholder="0.00"
              />
            </div>
          </div>
        )}

        {/* Reason for part-agree or dispute */}
        {(responseType === 'part_agreed' || responseType === 'disputed') && (
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>
              {responseType === 'disputed' ? 'Reasons for Dispute *' : 'Reason / Explanation *'}
            </label>
            <textarea
              value={responseReason}
              onChange={e => setResponseReason(e.target.value)}
              style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, minHeight: 100, boxSizing: 'border-box' }}
              placeholder={responseType === 'disputed' ? 'Explain specifically why you are disputing this variation (min 50 chars)...' : 'Explain the basis for your counter value...'}
            />
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
              {responseReason.trim().length} characters {responseReason.trim().length < 50 ? `(${50 - responseReason.trim().length} more required)` : '✓'}
            </div>
          </div>
        )}

        {formError && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#dc2626' }}>
            {formError}
          </div>
        )}

        <button
          onClick={handleSubmitResponse}
          disabled={processing || !responseType}
          style={{
            padding: '12px 32px', fontSize: 15, fontWeight: 700,
            background: responseType === 'agreed' ? '#16a34a' : responseType === 'part_agreed' ? '#d97706' : responseType === 'disputed' ? '#dc2626' : '#6b7280',
            color: '#fff', border: 'none', borderRadius: 6,
            cursor: (processing || !responseType) ? 'not-allowed' : 'pointer',
            opacity: (processing || !responseType) ? 0.6 : 1,
          }}
        >
          {processing ? 'Submitting…' : responseType ? `Confirm — ${responseOptions.find(o => o.id === responseType)?.label}` : 'Select a response above'}
        </button>
      </Card>
    </PortalShell>
  );
}

// ─── MC Portal: EWN / CVI Response ───────────────────────────────────────────

export function MCPortalEwnCvi({ instructionId, token }) {
  const [pageStatus, setPageStatus] = useState('loading');
  const [invalidMsg, setInvalidMsg] = useState('');
  const [responseRecord, setResponseRecord] = useState(null);
  const [instruction, setInstruction] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [respondentName, setRespondentName] = useState('');
  const [respondentPosition, setRespondentPosition] = useState('');
  const [respondentCompany, setRespondentCompany] = useState('');
  const [selectedResponse, setSelectedResponse] = useState(null);
  const [expectedDate, setExpectedDate] = useState('');
  const [queryText, setQueryText] = useState('');
  const [disputeText, setDisputeText] = useState('');
  const [notes, setNotes] = useState('');
  const [processing, setProcessing] = useState(false);
  const [formError, setFormError] = useState('');

  useEffect(() => {
    if (!token || !instructionId) { setInvalidMsg('Missing token or ID.'); setPageStatus('invalid'); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function load() {
    try {
      const { data: resp } = await supabase
        .from('ewn_cvi_responses')
        .select('*')
        .eq('token', token)
        .eq('instruction_id', instructionId)
        .maybeSingle();

      if (!resp) { setInvalidMsg('This link is invalid or has expired. Please contact Proline Internals Ltd.'); setPageStatus('invalid'); return; }
      if (resp.responded_at) { setPageStatus('already_responded'); return; }

      setResponseRecord(resp);
      setRespondentName(resp.recipient_name || '');
      setRespondentCompany(resp.recipient_company || '');

      const [{ data: ins }, { data: atts }] = await Promise.all([
        supabase.from('instructions').select('*, projects(id, name, reference, main_contractor)').eq('id', instructionId).single(),
        supabase.from('instruction_attachments').select('*').eq('instruction_id', instructionId).order('created_at'),
      ]);

      if (!ins) { setInvalidMsg('Instruction record not found.'); setPageStatus('invalid'); return; }

      // Generate signed URLs for attachments
      const attsWithUrls = await Promise.all((atts || []).map(async att => {
        try {
          const { data: urlData } = await supabase.storage.from('instruction-attachments').createSignedUrl(att.storage_path, 3600);
          return { ...att, signedUrl: urlData?.signedUrl || null };
        } catch {
          return { ...att, signedUrl: null };
        }
      }));

      setInstruction(ins);
      setAttachments(attsWithUrls);
      setPageStatus('valid');
    } catch (err) {
      setInvalidMsg('An error occurred loading this page: ' + err.message);
      setPageStatus('invalid');
    }
  }

  async function handleSubmit() {
    if (!respondentName.trim()) { setFormError('Please enter your full name.'); return; }
    if (!respondentPosition.trim()) { setFormError('Please enter your position/title.'); return; }
    if (!respondentCompany.trim()) { setFormError('Please enter your company name.'); return; }
    if (!selectedResponse) { setFormError('Please select a response option above.'); return; }
    if (selectedResponse === 'query' && !queryText.trim()) { setFormError('Please describe your query.'); return; }
    if (selectedResponse === 'disputed' && !disputeText.trim()) { setFormError('Please state the grounds for dispute.'); return; }

    setProcessing(true);
    setFormError('');

    try {
      const now = new Date().toISOString();
      const responseNotes = selectedResponse === 'query' ? queryText
        : selectedResponse === 'disputed' ? disputeText
        : notes || null;

      await supabase.from('ewn_cvi_responses').update({
        response: selectedResponse,
        response_notes: responseNotes || null,
        expected_instruction_date: selectedResponse === 'will_instruct' && expectedDate ? expectedDate : null,
        responded_at: now,
        respondent_name: respondentName.trim(),
        respondent_position: respondentPosition.trim(),
        respondent_company: respondentCompany.trim(),
      }).eq('token', token);

      const newStatus = selectedResponse === 'will_instruct' ? 'awaiting_instruction' : 'open';

      await supabase.from('instructions').update({
        mc_response: selectedResponse,
        mc_response_at: now,
        mc_response_notes: responseNotes || null,
        mc_expected_instruction_date: selectedResponse === 'will_instruct' && expectedDate ? expectedDate : null,
        status: newStatus,
      }).eq('id', instructionId);

      // Notify Proline
      try {
        const { subject, htmlBody } = ewnCviResponseNotification({
          instruction,
          response: selectedResponse,
          responseNotes: responseNotes || '',
          expectedDate: selectedResponse === 'will_instruct' ? expectedDate : null,
          respondedBy: respondentName.trim(),
          respondedCompany: respondentCompany.trim(),
          respondedAt: now,
        });
        await sendEmail({ to: 'notifications@prolineinternals.co.uk', subject, htmlBody });
      } catch (e) { console.error('Notification email failed:', e); }

      setPageStatus(selectedResponse);
    } catch (err) {
      setFormError('An error occurred: ' + err.message);
    } finally {
      setProcessing(false);
    }
  }

  if (pageStatus === 'loading') return <PortalShell><Card style={{ textAlign: 'center', padding: '40px' }}><p style={{ color: '#6b7280' }}>Loading…</p></Card></PortalShell>;
  if (pageStatus === 'invalid') return <InvalidPage message={invalidMsg} />;
  if (pageStatus === 'already_responded') return <AlreadyRespondedPage recordType="Notice" reference={instruction?.reference} />;

  if (pageStatus === 'will_instruct') return (
    <PortalShell>
      <Card style={{ textAlign: 'center', padding: '48px 24px', background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
        <h2 style={{ color: '#16a34a', marginBottom: 12 }}>Response Recorded</h2>
        <p style={{ color: '#374151', fontSize: 14, lineHeight: 1.6, maxWidth: 480, margin: '0 auto 8px' }}>
          Thank you — your response has been recorded.
        </p>
        <p style={{ color: '#374151', fontSize: 14, lineHeight: 1.6, maxWidth: 480, margin: '0 auto' }}>
          Proline Internals Ltd has been notified that you will issue a formal written instruction.
          Once received, Proline will record it against this notice.
        </p>
      </Card>
    </PortalShell>
  );

  if (pageStatus === 'query') return (
    <PortalShell>
      <Card style={{ textAlign: 'center', padding: '48px 24px', background: '#fffbeb', border: '1px solid #fde68a' }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>📋</div>
        <h2 style={{ color: '#d97706', marginBottom: 12 }}>Query Recorded</h2>
        <p style={{ color: '#374151', fontSize: 14, lineHeight: 1.6, maxWidth: 480, margin: '0 auto' }}>
          Thank you — your query has been recorded and Proline Internals Ltd has been notified.
          They will be in touch to resolve your query.
        </p>
      </Card>
    </PortalShell>
  );

  if (pageStatus === 'disputed') return (
    <PortalShell>
      <Card style={{ textAlign: 'center', padding: '48px 24px', background: '#fef2f2', border: '1px solid #fecaca' }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>📋</div>
        <h2 style={{ color: '#dc2626', marginBottom: 12 }}>Dispute Recorded</h2>
        <p style={{ color: '#374151', fontSize: 14, lineHeight: 1.6, maxWidth: 480, margin: '0 auto' }}>
          Thank you — your dispute has been recorded and Proline Internals Ltd has been notified.
        </p>
      </Card>
    </PortalShell>
  );

  const typeLabel = instruction.type === 'EWN' ? 'Early Warning Notice' : 'Confirmation of Verbal Instruction';
  const typeBadgeStyle = instruction.type === 'EWN'
    ? { background: '#fffbeb', color: '#92400e', border: '1px solid #fde68a' }
    : { background: '#dbeafe', color: '#1e40af', border: '1px solid #93c5fd' };

  const responseCards = [
    {
      id: 'will_instruct',
      label: 'We Will Issue a Formal Instruction',
      sub: 'Confirm you will issue a written instruction in response to this notice',
      border: '#bbf7d0', activeBorder: '#16a34a', activeBg: '#f0fdf4', activeColor: '#16a34a',
    },
    {
      id: 'query',
      label: 'We Have a Query',
      sub: 'You have a question before issuing an instruction',
      border: '#fde68a', activeBorder: '#d97706', activeBg: '#fffbeb', activeColor: '#d97706',
    },
    {
      id: 'disputed',
      label: 'We Dispute This Notice',
      sub: 'You dispute the basis of this notice',
      border: '#fecaca', activeBorder: '#dc2626', activeBg: '#fef2f2', activeColor: '#dc2626',
    },
  ];

  return (
    <PortalShell>
      {/* Header */}
      <Card style={{ background: '#111827', border: 'none', padding: '24px 28px' }}>
        <div style={{ marginBottom: 10 }}>
          <span style={{ ...typeBadgeStyle, display: 'inline-block', padding: '3px 10px', borderRadius: 4, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {instruction.type}
          </span>
        </div>
        <div style={{ fontSize: 26, fontWeight: 700, color: '#fff', fontFamily: 'monospace', marginBottom: 4 }}>
          {instruction.reference}
        </div>
        <div style={{ fontSize: 14, color: '#e5e7eb', marginBottom: 6 }}>{typeLabel}</div>
        <div style={{ fontSize: 13, color: '#cbd5e1' }}>
          {instruction.projects?.reference} — {instruction.projects?.name}
          {instruction.projects?.main_contractor && ` · ${instruction.projects.main_contractor}`}
        </div>
      </Card>

      {/* Notice details */}
      <Card>
        <SectionTitle>Notice Details</SectionTitle>
        <InfoRow label="Reference" value={instruction.reference} />
        <InfoRow label="Project" value={`${instruction.projects?.reference} — ${instruction.projects?.name}`} />
        {instruction.date && <InfoRow label="Date Raised" value={fmtDate(instruction.date)} />}
        {instruction.location && <InfoRow label="Location" value={instruction.location} />}
        {instruction.description && (
          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>
              {instruction.type === 'CVI' ? 'Works Verbally Instructed' : 'Description of Issue'}
            </div>
            <p style={{ fontSize: 13, color: '#374151', lineHeight: 1.6, margin: 0 }}>{instruction.description}</p>
          </div>
        )}
      </Card>

      {/* Contractual notice */}
      <Card style={{ background: '#fffbeb', border: '1px solid #f59e0b' }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <div style={{ fontSize: 20, flexShrink: 0 }}>⚠️</div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
              CONTRACTUAL NOTICE — RESPONSE REQUIRED
            </div>
            <p style={{ margin: 0, fontSize: 13, color: '#78350f', lineHeight: 1.7 }}>
              {instruction.type === 'EWN'
                ? `This Early Warning Notice is issued pursuant to the subcontract conditions. Proline Internals Ltd gives notice that the matter described may give rise to an increase in the Subcontract Sum and/or an extension of time. A formal written instruction is requested in response.`
                : `This Confirmation of Verbal Instruction is issued pursuant to the subcontract conditions. Proline Internals Ltd confirms the verbal instruction described above and requests a formal written instruction confirming the same.`}
            </p>
          </div>
        </div>
      </Card>

      {/* Attachments */}
      {attachments.length > 0 && (
        <Card>
          <SectionTitle>Attached Documents ({attachments.length})</SectionTitle>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {attachments.map(att => (
              <div key={att.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', background: '#f9fafb', borderRadius: 6, border: '1px solid #e5e7eb' }}>
                <div style={{ width: 36, height: 36, borderRadius: 6, background: '#dbeafe', color: '#1e40af', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, flexShrink: 0 }}>
                  {att.file_type === 'application/pdf' ? 'PDF' : att.file_type?.startsWith('image/') ? 'IMG' : 'DOC'}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: '#111' }}>{att.file_name}</div>
                </div>
                {att.signedUrl ? (
                  <a href={att.signedUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: ACCENT, fontWeight: 600, textDecoration: 'none', flexShrink: 0 }}>
                    Download
                  </a>
                ) : (
                  <span style={{ fontSize: 12, color: '#9ca3af', flexShrink: 0 }}>Contact Proline for copy</span>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Response section */}
      <Card style={{ border: '2px solid #e5e7eb' }}>
        <SectionTitle>Your Details & Response</SectionTitle>
        <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 20, marginTop: 0 }}>
          Please confirm your details and select your response. Your response will be formally recorded.
        </p>

        {/* Respondent details */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12, marginBottom: 20 }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Full Name *</label>
            <input value={respondentName} onChange={e => setRespondentName(e.target.value)}
              style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' }} placeholder="Your full name" />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Position / Title *</label>
            <input value={respondentPosition} onChange={e => setRespondentPosition(e.target.value)}
              style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' }} placeholder="e.g. Contract Manager, QS" />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Company *</label>
            <input value={respondentCompany} onChange={e => setRespondentCompany(e.target.value)}
              style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' }} placeholder="Your company name" />
          </div>
        </div>

        <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 16, marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8 }}>Response:</div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
          {responseCards.map(card => {
            const isSelected = selectedResponse === card.id;
            return (
              <div
                key={card.id}
                onClick={() => { setSelectedResponse(card.id); setFormError(''); }}
                style={{
                  padding: '16px 18px',
                  border: `2px solid ${isSelected ? card.activeBorder : '#e5e7eb'}`,
                  borderRadius: 8,
                  cursor: 'pointer',
                  background: isSelected ? card.activeBg : '#fff',
                  transition: 'all 0.1s',
                }}
              >
                <div style={{ fontWeight: 700, fontSize: 15, color: isSelected ? card.activeColor : '#374151', marginBottom: 4 }}>
                  {card.label}
                </div>
                <div style={{ fontSize: 13, color: '#6b7280' }}>{card.sub}</div>
              </div>
            );
          })}
        </div>

        {selectedResponse === 'will_instruct' && (
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>
              Expected instruction date:
            </label>
            <input
              type="date"
              value={expectedDate}
              onChange={e => setExpectedDate(e.target.value)}
              style={{ padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' }}
            />
          </div>
        )}

        {selectedResponse === 'query' && (
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>
              Please describe your query: *
            </label>
            <textarea
              value={queryText}
              onChange={e => setQueryText(e.target.value)}
              style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, minHeight: 100, boxSizing: 'border-box' }}
              placeholder="Describe your query in detail..."
            />
          </div>
        )}

        {selectedResponse === 'disputed' && (
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>
              Please state the grounds for dispute: *
            </label>
            <textarea
              value={disputeText}
              onChange={e => setDisputeText(e.target.value)}
              style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, minHeight: 100, boxSizing: 'border-box' }}
              placeholder="State specifically why you dispute this notice..."
            />
          </div>
        )}

        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>
            Additional comments (optional):
          </label>
          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, minHeight: 72, boxSizing: 'border-box' }}
            placeholder="Any additional comments..."
          />
        </div>

        {formError && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#dc2626' }}>
            {formError}
          </div>
        )}

        <button
          onClick={handleSubmit}
          disabled={processing || !selectedResponse}
          style={{
            padding: '13px 32px', fontSize: 15, fontWeight: 700,
            background: '#111827', color: '#fff', border: 'none', borderRadius: 6,
            cursor: (processing || !selectedResponse) ? 'not-allowed' : 'pointer',
            opacity: (processing || !selectedResponse) ? 0.6 : 1,
            width: '100%',
          }}
        >
          {processing ? 'Submitting…' : 'Submit Response'}
        </button>
      </Card>
    </PortalShell>
  );
}
